import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getToken, base_url } from "../../config/config";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { Pencil, X, FileText, Download } from "lucide-react";
import { stubFalse } from "lodash";

// Helper Functions
function calculateDuration(startTimeStr, endTimeStr) {
  const startParts = startTimeStr.split(":").map(Number);
  const endParts = endTimeStr.split(":").map(Number);
  if (startParts.length === 3 && endParts.length === 3) {
    let startSeconds =
      startParts[0] * 3600 + startParts[1] * 60 + startParts[2];
    let endSeconds = endParts[0] * 3600 + endParts[1] * 60 + endParts[2];

    // If end time is less than start time, it means the work crossed midnight
    if (endSeconds < startSeconds) {
      endSeconds += 24 * 3600; // Add 24 hours worth of seconds
    }

    const durationSeconds = endSeconds - startSeconds;

    // Convert duration to HH:MM:SS format
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    const seconds = durationSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  } else {
    console.warn(`Invalid time format for StartTime or EndTime.`);
    return "00:00:00";
  }
}
// Function to convert "H:M:S" to total seconds
function timeStringToSeconds(timeStr) {
  if (typeof timeStr !== "string") {
    console.warn(
      `TotalTime is not a string: "${timeStr}". Defaulting to 0 seconds.`
    );
    return 0;
  }

  const parts = timeStr.split(":").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    console.warn(
      `Invalid time format: "${timeStr}". Expected "H:M:S". Defaulting to 0 seconds.`
    );
    return 0;
  }
  const [hours, minutes, seconds] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}

// Function to format seconds back to "H:M:S"
function formatSeconds(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${minutes}:${seconds}`;
}

// Function to process project data
function processProjectData(projects) {
  return projects.map((project) => {
    let totalSeconds = 0;

    // Calculate TotalTime based on StartTime and EndTime
    if (project.StartTime && project.EndTime) {
      totalSeconds = calculateDuration(project.StartTime, project.EndTime);
    } else {
      console.warn(
        `Missing StartTime or EndTime for project with ANR ${project.ANR}`
      );
    }

    const totalHours = totalSeconds / 3600;
    const totalCost = totalHours * 60; // 60€ per hour

    // Prioritize MaxLohnaufwand if it exists
    const erwarteteTotalTime =
      project.MaxLohnaufwand !== undefined
        ? parseFloat(project.MaxLohnaufwand).toFixed(2) // Use MaxLohnaufwand
        : parseFloat(project.erwarteteTotalTime || 0).toFixed(2); // Fallback to erwarteteTotalTime

    const erwarteteTotalCost = parseFloat(erwarteteTotalTime * 60).toFixed(2);

    return {
      ...project,
      // Store TotalTime as decimal hours for simplicity
      TotalTime: project.TotalTime, // Store as a number
      TotalCost: totalCost.toFixed(2), // e.g., "150.00"
      gesamtCost: parseFloat(project.gesamtCost || 0).toFixed(2), // Ensure gesamtCost is a string with two decimals
      Auftragserlös: parseFloat(project.Auftragserlös || 0).toFixed(2), // Ensure Auftragserlös is a string with two decimals
      ErlossAusAngebot: parseFloat(project.ErlossAusAngebot || 0).toFixed(2),
      VoraussichtlicheMaterialkosten: parseFloat(
        project.VoraussichtlicheMaterialkosten || 0
      ).toFixed(2),
      MaterialCost: project.MaterialCost || "0.00",
      percentDone: parseFloat(project.percentDone || 0).toFixed(2),
      // Remove Erloss and update VoraussichtlicherEroess calculation
      VoraussichtlicherEroess: (
        parseFloat(project.ErlossAusAngebot || 0) -
        (parseFloat(project.MaterialCost || 0) ||
          parseFloat(project.VoraussichtlicheMaterialkosten || 0))
      ).toFixed(2),
      // New Fields
      erwarteteTotalTime, // Voraussichtliche Gesamter erfaßter Zeitaufwand (Std)
      erwarteteTotalCost, // Voraussichtliche Errechnete Gesamtlohnkosten (€)
      // Optionally, include MaxLohnaufwand for reference
      MaxLohnaufwand:
        project.MaxLohnaufwand !== undefined
          ? parseFloat(project.MaxLohnaufwand).toFixed(2)
          : null,
    };
  });
}

// Function to check if an ANR is a parent project (ends with 00 before year)
function isParentProject(anr) {
  // Regex pattern to match projects ending with -00-00-YY (YY being the year)
  const pattern = /\d+-00-00-\d+$/;
  return pattern.test(anr);
}

// Function to check if one ANR is parent of another
function isParentOf(parentANR, childANR) {
  if (!parentANR || !childANR) return false;

  // If parent isn't in parent format, it can't be a parent
  if (!isParentProject(parentANR)) return false;

  // Extract the base part of the potential parent (e.g., "0014" from "0014-00-00-24")
  const parentBase = parentANR.split("-")[0];
  const childBase = childANR.split("-")[0];

  // Child must have same base as parent
  if (parentBase !== childBase) return false;

  // Child should not be a parent project itself (not ending with -00-00-YY)
  if (isParentProject(childANR)) return false;

  // The year part (the last segment) should be the same
  const parentYear = parentANR.split("-").pop();
  const childYear = childANR.split("-").pop();

  return parentYear === childYear;
}

// Function to find parent ANR for a given project ANR
function findParentANR(anr, allANRs) {
  if (!anr) return null;

  // If it's already a parent, return itself
  if (isParentProject(anr)) return anr;

  // Try to find a parent
  const baseANR = anr.split("-")[0];
  const year = anr.split("-").pop();
  const potentialParent = `${baseANR}-00-00-${year}`;

  // Check if the potential parent exists in our data
  if (allANRs.includes(potentialParent)) {
    return potentialParent;
  }

  return null; // No parent found
}

// Function to merge data from both APIs based on ANR
function mergeDataByANR(apiData, projectData, materialCostMap, costEdits) {
  if (!apiData || !apiData.matchedPrefixes || !projectData) return apiData;

  // Get all ANRs from the data
  const allANRs = projectData.map((project) => project.ANR).filter(Boolean);

  // Create a mapping from ANR to aggregated project data
  const anrToProjectMap = {};

  // First pass: group subprojects by their parent ANR
  const subprojectToParentMap = {};

  projectData.forEach((project) => {
    if (project.ANR) {
      // Find the parent ANR for this project
      const parentANR = findParentANR(project.ANR, allANRs);

      if (parentANR && parentANR !== project.ANR) {
        // Store the parent-child relationship
        subprojectToParentMap[project.ANR] = parentANR;
      }
    }
  });

  // Second pass: aggregate time for both direct and parent projects
  projectData.forEach((project) => {
    if (project.ANR) {
      // Convert TotalTime to hours if it's in string format (HH:MM:SS)
      let projectTimeInHours = 0;
      if (
        typeof project.TotalTime === "string" &&
        project.TotalTime.match(/^\d{1,2}:\d{2}:\d{2}/)
      ) {
        const [hh, mm, ss] = project.TotalTime.split(":").map(Number);
        projectTimeInHours = hh + mm / 60;
      } else {
        projectTimeInHours = parseFloat(project.TotalTime) || 0;
      }

      // Initialize entry for this project's ANR if it doesn't exist
      if (!anrToProjectMap[project.ANR]) {
        anrToProjectMap[project.ANR] = {
          TotalTime: 0,
          // Initialize other aggregated fields as needed
        };
      }

      // Add time to this project's own entry
      anrToProjectMap[project.ANR].TotalTime += projectTimeInHours;

      // If this is a subproject, also add time to its parent
      const parentANR = subprojectToParentMap[project.ANR];
      if (parentANR) {
        if (!anrToProjectMap[parentANR]) {
          anrToProjectMap[parentANR] = {
            TotalTime: 0,
            // Initialize other aggregated fields as needed
          };
        }
        // Also add the time to parent project
        anrToProjectMap[parentANR].TotalTime += projectTimeInHours;
      }
    }
  });

  // Now in mergedProjects, we can get the aggregated data per ANR.
  // The anrToProjectMap is fully built at this point.

  const mergedPrefixes = apiData.matchedPrefixes.map((prefixData) => {
    // Create a map to store unique projects by ANR
    const uniqueProjectsMap = new Map();

    // Process all projects
    const processedProjects = prefixData.matchedProjects.map((project) => {
      const aggregatedData = anrToProjectMap[project.ANR] || {
        TotalTime: 0,
      };
      // Format finalTotalTime to hours (already converted during aggregation)
      let finalTotalTime = aggregatedData.TotalTime;

      // The aggregatedData.TotalTime is already in hours format from our aggregation above
      finalTotalTime = parseFloat(finalTotalTime) || 0;

      if (project.ANR == "0329-00-00-25") {
        console.log(aggregatedData);
        console.log(project.ANR, aggregatedData.TotalTime);
        console.log("finalTotalTime (hours)", finalTotalTime);
      }

      // Recalculate TotalCost based on finalTotalTime
      const finalTotalCost = (finalTotalTime * 60).toFixed(2); // 60€ per hour

      // Proceed with other calculations, including handling costEdits

      // Check if there are any cost edits for this project
      const edit = costEdits[project._id] || {};

      // Determine which Rechnungserlöß (gesamtCost) to use based on priority:
      // 1. Current edit in the UI
      // 2. Value from getallRechnungs API
      // 3. editedRechnungCost from database
      // 4. Original gesamtCost from project

      let finalGesamtCost = 0;

      if (edit.editedGesamtCost !== undefined) {
        // 1. First priority: Current edits in the UI
        finalGesamtCost = parseFloat(edit.editedGesamtCost);
      } else if (
        materialCostMap[project.ANR] &&
        typeof materialCostMap[project.ANR] === "object" &&
        materialCostMap[project.ANR].rechnungCost > 0
      ) {
        // 2. Second priority: Invoice data from getallRechnungs
        finalGesamtCost = parseFloat(materialCostMap[project.ANR].rechnungCost);
        // console.log(
        //   `Using rechnungCost from invoice for ${project.ANR}: ${finalGesamtCost}`
        // );

        // Also update the invoiceNo in the project if available
        if (materialCostMap[project.ANR].invoiceNo) {
          project.invoiceNo = materialCostMap[project.ANR].invoiceNo;
        }
      } else if (
        project.editedRechnungCost &&
        parseFloat(project.editedRechnungCost) > 0
      ) {
        // 3. Third priority: editedRechnungCost from database
        finalGesamtCost = parseFloat(project.editedRechnungCost);
        // console.log(
        //   `Using editedRechnungCost from database for ${project.ANR}: ${finalGesamtCost}`
        // );
      } else {
        // 4. Last priority: Original gesamtCost
        finalGesamtCost = parseFloat(project.gesamtCost || 0);
      }

      // Determine which Material Cost to use based on priority:
      // 1. Current edit in the UI
      // 2. Value from getallRechnungs API (materialCostMap)
      // 3. editedMaterialCost from database
      // 4. Original MaterialCost from project

      let finalMaterialCost = 0;

      if (edit.editedMaterialCost !== undefined) {
        // 1. First priority: Current edits in the UI
        finalMaterialCost = parseFloat(edit.editedMaterialCost);
      } else if (materialCostMap[project.ANR]) {
        // 2. Second priority: Invoice data from getallRechnungs
        // Check if it's an object with materialCost or just a number
        if (
          typeof materialCostMap[project.ANR] === "object" &&
          materialCostMap[project.ANR].materialCost !== undefined
        ) {
          finalMaterialCost = parseFloat(
            materialCostMap[project.ANR].materialCost
          );
        } else {
          finalMaterialCost = parseFloat(materialCostMap[project.ANR]);
        }
      } else if (
        project.editedMaterialCost &&
        parseFloat(project.editedMaterialCost) > 0
      ) {
        // 3. Third priority: editedMaterialCost from database
        finalMaterialCost = parseFloat(project.editedMaterialCost);
      } else {
        // 4. Last priority: Original MaterialCost
        finalMaterialCost = parseFloat(project.MaterialCost || 0);
      }

      const finalErlossAusAngebot =
        edit.editedErlossAusAngebot !== undefined
          ? parseFloat(edit.editedErlossAusAngebot)
          : parseFloat(project.ErlossAusAngebot || 0);

      const finalVoraussichtlicheMaterialkosten =
        edit.editedVoraussichtlicheMaterialkosten !== undefined
          ? parseFloat(edit.editedVoraussichtlicheMaterialkosten)
          : parseFloat(project.VoraussichtlicheMaterialkosten || 0);

      // Calculate VoraussichtlicherEroess using the new formula or edited value
      const finalVoraussichtlicherEroess =
        edit.editedVoraussichtlicherEroess !== undefined
          ? parseFloat(edit.editedVoraussichtlicherEroess).toFixed(2)
          : (
              finalErlossAusAngebot -
              (finalMaterialCost || finalVoraussichtlicheMaterialkosten)
            ).toFixed(2);

      return {
        ...project,
        TotalTime: finalTotalTime.toFixed(2),
        TotalCost: finalTotalCost,
        gesamtCost: finalGesamtCost.toFixed(2),
        MaterialCost: finalMaterialCost.toFixed(2),
        ErlossAusAngebot: finalErlossAusAngebot.toFixed(2),
        VoraussichtlicheMaterialkosten:
          finalVoraussichtlicheMaterialkosten.toFixed(2),
        VoraussichtlicherEroess: finalVoraussichtlicherEroess,
        // Other fields...
      };
    });

    return {
      ...prefixData,
      matchedProjects: processedProjects,
    };
  });

  // Process each prefix to deduplicate projects by ANR
  const dedupedPrefixes = mergedPrefixes.map((prefix) => {
    // Create a map to store unique projects by ANR
    const anrMap = new Map();

    // Process each project and keep only one per ANR
    prefix.matchedProjects.forEach((project) => {
      if (!project.ANR) return; // Skip projects without ANR

      if (!anrMap.has(project.ANR)) {
        // First time seeing this ANR
        anrMap.set(project.ANR, project);
      } else {
        // We've seen this ANR before, decide which one to keep
        const existingProject = anrMap.get(project.ANR);
        const existingHasInvoice =
          existingProject.invoiceNo !== "Keine Rechnung gefunden";
        const currentHasInvoice =
          project.invoiceNo !== "Keine Rechnung gefunden";

        // Prefer projects with invoices
        if (!existingHasInvoice && currentHasInvoice) {
          anrMap.set(project.ANR, project);
        }
        // If both have invoices or both don't, prefer the one with the most recent timestamp
        else if (existingHasInvoice === currentHasInvoice) {
          const existingDate = new Date(existingProject.createdAt || 0);
          const currentDate = new Date(project.createdAt || 0);

          if (currentDate > existingDate) {
            anrMap.set(project.ANR, project);
          }
        }
      }
    });

    // Convert the map back to an array
    const dedupedProjects = Array.from(anrMap.values());

    return {
      ...prefix,
      matchedProjects: dedupedProjects,
    };
  });

  return {
    ...apiData,
    matchedPrefixes: dedupedPrefixes,
  };
}

// Function to aggregate material costs and invoice data from Rechnungs data
function aggregateMaterialCosts(rechnungsData) {
  const materialCostMap = {};

  rechnungsData.forEach((rechnung) => {
    // Get the total invoice amount if available
    const invoiceTotal = parseFloat(
      rechnung.amount || rechnung.totalAmount || 0
    );
    const invoiceNumber =
      rechnung.invoiceNo || rechnung.rechnungNumber || rechnung.number || "";

    if (rechnung.items && Array.isArray(rechnung.items)) {
      // Process individual items
      rechnung.items.forEach((item) => {
        const anr = item.ANR;
        if (!anr) return; // Skip items without ANR

        // Calculate material cost
        const totalStuckCost = parseFloat(item.totalStuckCost) || 0;

        // Initialize ANR entry if not exists
        if (!materialCostMap[anr]) {
          materialCostMap[anr] = {
            materialCost: 0,
            rechnungCost: 0, // For storing invoice amount for this ANR
            invoiceNo: invoiceNumber, // Store invoice number
            invoiceDate: rechnung.date || rechnung.rechnungDate || null,
          };
        }

        // Add material cost
        materialCostMap[anr].materialCost += totalStuckCost;

        // If there's only one ANR in this invoice, assign the full invoice amount to it
        const anrCount = new Set(
          rechnung.items.map((i) => i.ANR).filter(Boolean)
        ).size;
        if (anrCount === 1 && invoiceTotal > 0) {
          materialCostMap[anr].rechnungCost = invoiceTotal;
          // console.log(
          //   `Assigning full invoice amount ${invoiceTotal} to ANR ${anr}`
          // );
        }
        // If multiple ANRs in the invoice, distribute proportionally to material costs
        else if (anrCount > 1 && invoiceTotal > 0) {
          // Sum all material costs for this invoice
          const invoiceMaterialTotal = rechnung.items.reduce(
            (sum, i) => sum + (parseFloat(i.totalStuckCost) || 0),
            0
          );

          if (invoiceMaterialTotal > 0) {
            // Calculate this ANR's share of the invoice total
            const proportion = totalStuckCost / invoiceMaterialTotal;
            materialCostMap[anr].rechnungCost += invoiceTotal * proportion;
            // console.log(
            //   `Assigning proportional invoice amount ${
            //     invoiceTotal * proportion
            //   } to ANR ${anr}`
            // );
          }
        }
      });
    }
    // If the invoice has an ANR but no items, assign the total directly
    else if (rechnung.ANR && invoiceTotal > 0) {
      const anr = rechnung.ANR;
      if (!materialCostMap[anr]) {
        materialCostMap[anr] = {
          materialCost: 0,
          rechnungCost: invoiceTotal,
          invoiceNo: invoiceNumber,
          invoiceDate: rechnung.date || rechnung.rechnungDate || null,
        };
      } else {
        materialCostMap[anr].rechnungCost += invoiceTotal;
      }
      // console.log(
      //   `Assigning direct invoice amount ${invoiceTotal} to ANR ${anr}`
      // );
    }
  });

  // For backward compatibility, convert to simple number format if no rechnungCost is found
  const finalMap = {};
  for (const anr in materialCostMap) {
    if (materialCostMap[anr].rechnungCost > 0) {
      finalMap[anr] = materialCostMap[anr]; // Keep the object with all the data
    } else {
      finalMap[anr] = materialCostMap[anr].materialCost; // Just use the material cost number
    }
  }

  return finalMap;
}

// Enhanced getColumnLetters to handle more than 26 columns (AA, AB, etc.)
const getColumnLetters = (num) => {
  const letters = [];
  for (let i = 0; i < num; i++) {
    let temp = "";
    let dividend = i + 1;
    while (dividend > 0) {
      let modulo = (dividend - 1) % 26;
      temp = String.fromCharCode(65 + modulo) + temp;
      dividend = Math.floor((dividend - modulo) / 26);
    }
    letters.push(temp);
  }
  return letters;
};

// Updated Column Definitions
const allColumns = [
  { key: "ANR", label: "ANR" },
  { key: "Auftragsbezeichnung", label: "Auftragsbezeichnung" },
  { key: "invoiceNo", label: "Rechnung Nummer" },
  { key: "status", label: "Status" },
  {
    key: "percentDone",
    label: "Nach geplanten Arbeitsstunden errechneter % Fortschritt",
  },
  { key: "gesamtCost", label: "Rechnungserlöß" },
  { key: "Auftragserlös", label: "Auftragserlöß" },
  { key: "TotalTime", label: "Gesamter erfaßter Zeitaufwand (Std)" }, // Decimal hours
  {
    key: "calculatedLaborCost",
    label: "Errechnete Arbeitskosten (€)",
  },
  { key: "MaterialCost", label: "Materialkosten (€)" }, // MaterialCost column
  {
    key: "totalCombinedCost",
    label: "Gesamtkosten (€)",
  }, // Combined costs
  { key: "calculatedProfitLoss", label: "Profit/Loss (€)" },
  // Add more columns as needed
];
const RechnungMatcher = () => {
  // All existing state variables remain the same
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectError, setProjectError] = useState(null);
  const [rechnungsData, setRechnungsData] = useState(null);
  const [rechnungsLoading, setRechnungsLoading] = useState(true);
  const [rechnungsError, setRechnungsError] = useState(null);
  const [mergedData, setMergedData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedANR, setSelectedANR] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [costEdits, setCostEdits] = useState({});
  const [selectedProjects, setSelectedProjects] = useState([]);
  const globalRowCounter = useRef(1);
  const [showAllTime, setShowAllTime] = useState(false);

  // New state variables for the redesign
  const [viewMode, setViewMode] = useState("table"); // "table" or "cards"
  const [profitLossView, setProfitLossView] = useState("both"); // "profit", "loss", "both"
  const [expandedProjects, setExpandedProjects] = useState({}); // Track which project cards are expanded

  // State for grand totals
  const [grandTotalMaterialCost, setGrandTotalMaterialCost] = useState(0);
  const [grandTotalErwarteteTotalCost, setGrandTotalErwarteteTotalCost] =
    useState(0);
  const [grandTotalSeconds, setGrandTotalSeconds] = useState(0);
  const [grandProfitOrLoss, setGrandProfitOrLoss] = useState(0);
  const [aggregatedProjectData, setAggregatedProjectData] = useState({});

  const [InvoiceNo, setInvoiceNo] = useState("");
  const [currentProject, setcurrentProject] = useState(0);
  const [ShowInvoiceModal, setShowInvoiceModal] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${base_url}/v1/UpdateProjectRechnung/${currentProject}`,
        { InvoiceNumber: InvoiceNo },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      intialFetch();
      setShowInvoiceModal(false);
    } catch (err) {
      console.error("Save error:", err);
      alert(`Update failed: ${err.message || "Unknown error"}`);
      setShowInvoiceModal(false);
    }
  };

  const handleUpdateInvoiceClick = (projectid, InvoiceNo) => {
    setcurrentProject(projectid);
    setInvoiceNo(InvoiceNo);
    setShowInvoiceModal(true);
  };

  const renderInvoiceModal = () => {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
          <button
            onClick={() => setShowInvoiceModal(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Rechnung aktualisieren
          </h2>

          <div className="mb-6">
            <input
              type="text"
              value={InvoiceNo}
              onChange={(e) => {
                setInvoiceNo(e.target.value);
              }}
              placeholder="Geben Sie die Rechnungsnummer ein"
              className="w-full px-4 py-3 border border-gray-300 rounded-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowInvoiceModal(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Abbrechen
            </button>
            <button
              onClick={() => handleSave()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Speichern
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Calculate labor cost based on TotalTime and MengeCost (hourly rate)
  const calculateLaborCost = (project) => {
    const timeInHours = parseFloat(project.TotalTime) || 0;
    const hourlyRate = parseFloat(project.MengeCost || 60); // Use MengeCost if available, otherwise default to 60€
    return timeInHours * hourlyRate;
  };

  // Calculate profit or loss for a single project
  const calculateProjectProfitLoss = (project) => {
    const laborCost = calculateLaborCost(project);
    const materialCost = parseFloat(project.MaterialCost) || 0;
    const gesamtCost = parseFloat(project.gesamtCost) || 0;
    const Auftragserlös = parseFloat(project.Auftragserlös) || 0;

    return gesamtCost == 0
      ? Auftragserlös || 0
      : gesamtCost - (laborCost + materialCost);
  };

  // Filter projects based on profit/loss view
  const filterByProfitLoss = (projects) => {
    if (profitLossView === "both") return projects;

    return projects.filter((project) => {
      const profitLoss = calculateProjectProfitLoss(project);
      if (profitLossView === "profit") return profitLoss >= 0;
      if (profitLossView === "loss") return profitLoss < 0;
      return true;
    });
  };

  // Function to toggle card expansion for a project
  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // All existing functions remain the same
  const resetGlobalRowCounter = () => {
    globalRowCounter.current = 1;
  };

  // Function to get global row number
  const getGlobalRowNumber = () => {
    return globalRowCounter.current++;
  };

  // Export functions
  const exportToExcel = () => {
    if (!mergedData) return;

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // Add headers
    const headers = [
      "ANR",
      "Auftragsbezeichnung",
      "Menge",
      "Kunden",
      "Status",
      "Auftragserteilungsdatum",
      "Fertigstellungstermin",
      "Kostenstelle",
      "Lieferdatum",
      "Rechnung Nr.",
      "MaxLohnFund",
      "Stückkosten",
      "TotalTime",
      "CalculatedTimeCost",
      "MaterialCost",
      "Geschätzter Preis",
      "Auftragswert (netto)",
      "VoraussichtlicherEroess",
      "MeßwerkzeugeStatus",
      "MaterialStatus",
      "WerkszeugnisseStatus",
      "TransportStatus",
      "MusterlieferungStatus",
    ];
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });

    // Add data
    const data = filteredMatchedPrefixes?.flatMap((prefix) =>
      prefix.matchedProjects.map((project) => {
        const timeInHours = parseFloat(project.TotalTime) || 0;
        const hourlyRate = parseFloat(project.MengeCost || 60);
        const calculatedTimeCost = (timeInHours * hourlyRate).toFixed(2);

        return [
          project.ANR,
          project.Auftragsbezeichnung,
          project.Menge || "",
          project.kundenID?.bezeichnung || "",
          project.status,
          project.deadline || "",
          project.completionDate || "",
          project.kostenstelle || "",
          project.Lieferdatum || "",
          project.invoiceNo || "",
          project.MaxLohnaufwand || "",
          project.MengeCost || "",
          project.TotalTime,
          calculatedTimeCost,
          project.MaterialCost,
          project.estimatedPrice || "",
          project.auftragswertNetto || "",
          project.VoraussichtlicherEroess,
          project.MeßwerkzeugeStatus || "",
          project.MaterialStatus || "",
          project.WerkszeugnisseStatus || "",
          project.TransportStatus || "",
          project.MusterlieferungStatus || "",
        ];
      })
    );

    XLSX.utils.sheet_add_aoa(worksheet, data, { origin: "A2" });
    XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "project_data.xlsx");
  };

  // Update the exportToPDF function (around line 792):
  const exportToPDF = () => {
    if (!mergedData) return;

    try {
      const doc = new jsPDF();

      // Set title
      doc.setFontSize(18);
      doc.text("Project Data Report", 14, 22);

      // Create table
      const tableColumn = [
        "ANR",
        "Description",
        "Status",
        "Time (hrs)",
        "Material (€)",
        "Profit/Loss (€)",
      ];

      // Fix: Add null/undefined checks and provide fallbacks
      const tableRows = filteredMatchedPrefixes?.flatMap((prefix) =>
        prefix.matchedProjects.map((project) => [
          project.ANR || "N/A",
          // Fix: Add null check and provide fallback before calling substring
          (project.Auftragsbezeichnung || "No Description").substring(0, 20),
          project.status || "N/A",
          project.TotalTime || "0",
          project.MaterialCost || "0",
          project.VoraussichtlicherEroess || "0",
        ])
      );

      doc.autoTable({
        head: [tableColumn],
        body: tableRows || [], // Add fallback for empty data
        startY: 30,
        theme: "grid",
        styles: { fontSize: 8 },
        // Add these options to handle large datasets better
        pageBreak: "auto",
        margin: { top: 30 },
        didDrawPage: function (data) {
          // Add page numbers
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber}`,
            doc.internal.pageSize.width - 30,
            doc.internal.pageSize.height - 10
          );
        },
      });

      doc.save("project_data.pdf");
    } catch (error) {
      console.error("PDF generation error:", error);
      alert(
        "Error generating PDF. The dataset may be too large. Try filtering the data or use Excel export instead."
      );
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    if (!bytes) return "N/A";

    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
  };

  // Comprehensive Project Booklet Export
  // Enhanced Project Booklet Export Function with CNC Heidenau Branding
  // Add this function to your RechnungMatcher component

  const exportProjectBooklet = async (project) => {
    try {
      console.log("Starting export for project:", project.ANR);

      // Validate project data
      if (!project || !project.ANR) {
        throw new Error("Ungültiges Projekt: ANR fehlt");
      }

      setLoading(true);

      // Fetch comprehensive project data
      let timeLogs = [];
      let projectDetails = {};
      let invoices = [];
      let materialInvoices = [];

      try {
        const timeLogsResponse = await axios.get(
          `${base_url}/v1/getUserProjectbyANR2/${project.ANR}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        timeLogs = timeLogsResponse.data?.data?.userProjects || [];
      } catch (error) {
        console.warn("Could not fetch time logs:", error.message);
      }

      try {
        const projectDetailsResponse = await axios.post(
          `${base_url}/v1/getProjectbyANR`,
          { ANR: project.ANR },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        projectDetails = projectDetailsResponse.data?.data?.[0] || {};
      } catch (error) {
        console.warn("Could not fetch project details:", error.message);
        projectDetails = project;
      }

      // Create PDF with A4 size
      let doc;
      try {
        doc = new jsPDF("p", "mm", "a4");
      } catch (pdfError) {
        throw new Error(
          "Fehler beim Erstellen der PDF-Datei: " + pdfError.message
        );
      }
      let yPosition = 20;
      const pageHeight = 280;
      const leftMargin = 20;
      const rightMargin = 190;
      const contentWidth = rightMargin - leftMargin;

      // Company colors
      const primaryColor = [0, 51, 102]; // Dark blue
      const accentColor = [229, 0, 0]; // Red accent
      const successColor = [0, 128, 0]; // Green
      const warningColor = [255, 140, 0]; // Orange

      // Helper functions
      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight) {
          addPageFooter();
          doc.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      const addPageFooter = () => {
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Seite ${pageNumber}`, 105, 290, { align: "center" });
        doc.setDrawColor(200, 200, 200);
        doc.line(20, 285, 190, 285);
        doc.text("CNC Heidenau GmbH", 20, 290);
        doc.text(new Date().toLocaleDateString("de-DE"), 190, 290, {
          align: "right",
        });
      };

      const addSectionHeader = (title, icon = null) => {
        checkNewPage(30);

        // Section background
        doc.setFillColor(245, 247, 250);
        doc.rect(leftMargin - 5, yPosition - 5, contentWidth + 10, 12, "F");

        // Section title
        doc.setFontSize(14);
        doc.setFont(undefined, "bold");
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(title, leftMargin, yPosition + 3);
        yPosition += 15;

        // Decorative line
        doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
        doc.setLineWidth(0.5);
        doc.line(leftMargin, yPosition, leftMargin + 30, yPosition);
        yPosition += 8;

        doc.setFont(undefined, "normal");
        doc.setTextColor(0, 0, 0);
        doc.setLineWidth(0.2);
      };

      // Add company logo function
      const addCompanyLogo = async () => {
        try {
          // Try to load the company logo
          const logoUrl = "https://cnc-heidenau.de/loginpage.jpg";
          const img = new Image();
          img.crossOrigin = "anonymous";

          return new Promise((resolve) => {
            img.onload = function () {
              // Calculate aspect ratio
              const imgWidth = 40;
              const imgHeight = (img.height / img.width) * imgWidth;
              doc.addImage(img, "JPEG", 85, 30, imgWidth, imgHeight);
              resolve();
            };
            img.onerror = function () {
              // Fallback: Draw a placeholder logo
              doc.setDrawColor(
                primaryColor[0],
                primaryColor[1],
                primaryColor[2]
              );
              doc.setLineWidth(2);
              doc.rect(85, 30, 40, 20);
              doc.setFontSize(12);
              doc.setTextColor(
                primaryColor[0],
                primaryColor[1],
                primaryColor[2]
              );
              doc.text("CNC", 105, 42, { align: "center" });
              doc.text("HEIDENAU", 105, 47, { align: "center" });
              resolve();
            };
            img.src = logoUrl;
          });
        } catch (error) {
          console.warn("Could not load company logo");
          return Promise.resolve();
        }
      };

      // === TITLE PAGE ===
      // Header background
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, 210, 60, "F");

      // Add logo
      await addCompanyLogo();

      // Title
      doc.setFontSize(28);
      doc.setFont(undefined, "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("PROJEKTBERICHT", 105, 80, { align: "center" });

      // Project info box
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(30, 100, 150, 60, 3, 3, "F");

      doc.setFontSize(20);
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text(project.ANR, 105, 115, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(60, 60, 60);
      const titleLines = doc.splitTextToSize(
        project.Auftragsbezeichnung || "Keine Beschreibung",
        140
      );
      let titleY = 130;
      titleLines.forEach((line) => {
        doc.text(line, 105, titleY, { align: "center" });
        titleY += 7;
      });

      // Company info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("CNC Heidenau GmbH", 105, 180, { align: "center" });
      doc.text("Dresdner Straße 78", 105, 187, { align: "center" });
      doc.text("01809 Heidenau", 105, 194, { align: "center" });
      doc.text("Deutschland", 105, 201, { align: "center" });

      // Report date
      doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.rect(60, 220, 90, 10, "F");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(
        `Bericht erstellt am: ${new Date().toLocaleDateString("de-DE")}`,
        105,
        226,
        { align: "center" }
      );

      // Start new page for content
      doc.addPage();
      yPosition = 30;

      // === EXECUTIVE SUMMARY ===
      addSectionHeader("ZUSAMMENFASSUNG");

      // Create summary cards
      const summaryData = {
        status: project.status || "In Bearbeitung",
        progress: parseFloat(project.percentDone || 0),
        totalHours: parseFloat(project.TotalTime || 0),
        materialCost: parseFloat(project.MaterialCost || 0),
        laborCost: parseFloat(
          project.TotalCost || calculateLaborCost(project) || 0
        ),
        revenue: parseFloat(project.gesamtCost || project.Auftragserlös || 0),
      };

      const profitLoss =
        summaryData.revenue -
        (summaryData.materialCost + summaryData.laborCost);
      const isProfitable = profitLoss >= 0;

      // Status indicator
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(leftMargin, yPosition, contentWidth, 40, 3, 3, "F");

      // Progress bar
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text("Projektfortschritt:", leftMargin + 5, yPosition + 8);

      // Progress bar background
      doc.setFillColor(220, 220, 220);
      doc.roundedRect(
        leftMargin + 5,
        yPosition + 12,
        contentWidth - 10,
        8,
        2,
        2,
        "F"
      );

      // Progress bar fill
      const progressWidth = ((contentWidth - 10) * summaryData.progress) / 100;
      const progressColor =
        summaryData.progress >= 75
          ? successColor
          : summaryData.progress >= 50
          ? warningColor
          : accentColor;
      doc.setFillColor(progressColor[0], progressColor[1], progressColor[2]);
      doc.roundedRect(
        leftMargin + 5,
        yPosition + 12,
        progressWidth,
        8,
        2,
        2,
        "F"
      );

      // Progress percentage
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(
        `${summaryData.progress.toFixed(1)}%`,
        rightMargin - 5,
        yPosition + 8,
        { align: "right" }
      );

      // Profit/Loss indicator
      const profitabilityFillColor = isProfitable
        ? [220, 252, 231]
        : [254, 226, 226];
      doc.setFillColor(
        profitabilityFillColor[0],
        profitabilityFillColor[1],
        profitabilityFillColor[2]
      );
      doc.roundedRect(
        leftMargin + 5,
        yPosition + 25,
        contentWidth - 10,
        10,
        2,
        2,
        "F"
      );
      const profitabilityTextColor = isProfitable ? successColor : accentColor;
      doc.setTextColor(
        profitabilityTextColor[0],
        profitabilityTextColor[1],
        profitabilityTextColor[2]
      );
      doc.setFontSize(11);
      doc.text(
        `${isProfitable ? "Gewinn" : "Verlust"}: €${Math.abs(
          profitLoss
        ).toFixed(2)}`,
        105,
        yPosition + 31,
        { align: "center" }
      );

      yPosition += 50;

      // === PROJECT OVERVIEW ===
      addSectionHeader("PROJEKTÜBERSICHT");

      const overviewData = [
        ["Projektnummer", project.ANR],
        ["Projektbezeichnung", project.Auftragsbezeichnung || "N/A"],
        ["Status", project.status || "N/A"],
        ["Kunde", projectDetails.kundenID?.name || "N/A"],
        ["Lieferant", projectDetails.lieferantID?.name || "N/A"],
        [
          "Lieferdatum",
          projectDetails.Lieferdatum
            ? new Date(projectDetails.Lieferdatum).toLocaleDateString("de-DE")
            : "N/A",
        ],
        ["Kostenstelle", projectDetails.kostenstelle || "N/A"],
        ["Artikelnummer", projectDetails.Artikelnummer || "N/A"],
        ["Menge", projectDetails.Menge || "N/A"],
      ];

      doc.autoTable({
        body: overviewData,
        startY: yPosition,
        theme: "plain",
        styles: {
          fontSize: 10,
          cellPadding: 4,
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 60, textColor: [60, 60, 60] },
          1: { cellWidth: 100 },
        },
        margin: { left: leftMargin, right: 20 },
        didDrawCell: (data) => {
          if (data.column.index === 0) {
            doc.setDrawColor(220, 220, 220);
            doc.line(
              data.cell.x + data.cell.width,
              data.cell.y,
              data.cell.x + data.cell.width,
              data.cell.y + data.cell.height
            );
          }
        },
      });

      yPosition = doc.lastAutoTable.finalY + 15;

      // === FINANCIAL SUMMARY ===
      addSectionHeader("FINANZIELLER ÜBERBLICK");

      // Financial cards
      const financialCards = [
        {
          label: "Erlös aus Angebot",
          value: project.ErlossAusAngebot || project.Auftragserlös || 0,
          color: primaryColor,
        },
        {
          label: "Materialkosten",
          value: summaryData.materialCost,
          color: warningColor,
        },
        {
          label: "Arbeitskosten",
          value: summaryData.laborCost,
          color: [128, 0, 128],
        },
        {
          label: "Gesamtkosten",
          value: summaryData.materialCost + summaryData.laborCost,
          color: accentColor,
        },
        {
          label: isProfitable ? "Gewinn" : "Verlust",
          value: Math.abs(profitLoss),
          color: isProfitable ? successColor : accentColor,
        },
      ];

      let cardX = leftMargin;
      const cardWidth = 30;
      const cardHeight = 25;

      financialCards.forEach((card, index) => {
        // Card background
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(cardX, yPosition, cardWidth, cardHeight, 2, 2, "F");

        // Card value
        doc.setFontSize(14);
        doc.setFont(undefined, "bold");
        doc.setTextColor(card.color[0], card.color[1], card.color[2]);
        doc.text(
          `€${parseFloat(card.value).toFixed(2)}`,
          cardX + cardWidth / 2,
          yPosition + 10,
          { align: "center" }
        );

        // Card label
        doc.setFontSize(8);
        doc.setFont(undefined, "normal");
        doc.setTextColor(100, 100, 100);
        const labelLines = doc.splitTextToSize(card.label, cardWidth - 4);
        labelLines.forEach((line, i) => {
          doc.text(line, cardX + cardWidth / 2, yPosition + 16 + i * 4, {
            align: "center",
          });
        });

        cardX += cardWidth + 4;
      });

      yPosition += cardHeight + 15;

      // === TIME LOGS ===
      if (timeLogs.length > 0) {
        checkNewPage(60);
        addSectionHeader("ARBEITSZEITERFASSUNG");

        // Calculate total hours and cost
        let totalMinutes = 0;
        const processedLogs = timeLogs.map((log) => {
          const duration = log.TotalTime || "00:00:00";
          const [hours, minutes, seconds] = duration.split(":").map(Number);
          const logMinutes = hours * 60 + minutes + seconds / 60;
          totalMinutes += logMinutes;

          return [
            new Date(log.date).toLocaleDateString("de-DE"),
            log.user_id?.name || log.user || "N/A",
            log.Arbeiten || "N/A",
            log.description || "N/A",
            log.StartTime || "N/A",
            log.EndTime || "N/A",
            duration,
            log.Menge || "-",
          ];
        });

        const totalHours = totalMinutes / 60;
        const estimatedCost = totalHours * 60; // 60€ per hour

        // Summary box
        doc.setFillColor(245, 247, 250);
        doc.roundedRect(leftMargin, yPosition, contentWidth, 15, 2, 2, "F");
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        doc.text(
          `Gesamtstunden: ${totalHours.toFixed(2)} Std`,
          leftMargin + 5,
          yPosition + 6
        );
        doc.text(
          `Geschätzte Kosten: €${estimatedCost.toFixed(2)}`,
          leftMargin + 5,
          yPosition + 11
        );
        doc.text(
          `Anzahl Einträge: ${timeLogs.length}`,
          rightMargin - 5,
          yPosition + 6,
          { align: "right" }
        );

        yPosition += 20;

        // Time logs table
        doc.autoTable({
          head: [
            [
              "Datum",
              "Mitarbeiter",
              "Tätigkeit",
              "Beschreibung",
              "Start",
              "Ende",
              "Dauer",
              "Menge",
            ],
          ],
          body: processedLogs,
          startY: yPosition,
          theme: "striped",
          styles: {
            fontSize: 8,
            cellPadding: 2,
            lineColor: [220, 220, 220],
            lineWidth: 0.1,
          },
          headStyles: {
            fillColor: [...primaryColor],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [248, 248, 248],
          },
          columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 25 },
            2: { cellWidth: 25 },
            3: { cellWidth: 40 },
            4: { cellWidth: 15 },
            5: { cellWidth: 15 },
            6: {
              cellWidth: 18,
              fontStyle: "bold",
              textColor: [...primaryColor],
            },
            7: { cellWidth: 12 },
          },
          margin: { left: leftMargin, right: 20 },
          didDrawPage: function (data) {
            // Add header to new pages
            if (data.pageNumber > 1) {
              doc.setFontSize(10);
              doc.setTextColor(100, 100, 100);
              doc.text("Arbeitszeiterfassung (Fortsetzung)", leftMargin, 15);
            }
          },
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // === MATERIAL COSTS ===
      if (
        projectDetails.materialCosts?.length > 0 ||
        summaryData.materialCost > 0
      ) {
        checkNewPage(40);
        addSectionHeader("MATERIALKOSTEN");

        const materialData = projectDetails.materialCosts?.map((material) => [
          material.name || "N/A",
          material.description || "N/A",
          material.supplier || "N/A",
          material.quantity || "-",
          `€${parseFloat(material.cost || 0).toFixed(2)}`,
        ]) || [
          [
            "Gesamte Materialkosten",
            "Zusammengefasste Materialkosten für das Projekt",
            "-",
            "-",
            `€${summaryData.materialCost.toFixed(2)}`,
          ],
        ];

        doc.autoTable({
          head: [["Material", "Beschreibung", "Lieferant", "Menge", "Kosten"]],
          body: materialData,
          startY: yPosition,
          theme: "grid",
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [...primaryColor],
            textColor: 255,
          },
          columnStyles: {
            4: { fontStyle: "bold", halign: "right" },
          },
          margin: { left: leftMargin, right: 20 },
          foot: [
            [
              {
                content: "Gesamt:",
                colSpan: 4,
                styles: { halign: "right", fontStyle: "bold" },
              },
              {
                content: `€${summaryData.materialCost.toFixed(2)}`,
                styles: { fontStyle: "bold" },
              },
            ],
          ],
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // === INVOICES ===
      const hasInvoice =
        project.invoiceNo && project.invoiceNo !== "Keine Rechnung gefunden";
      if (hasInvoice) {
        checkNewPage(30);
        addSectionHeader("RECHNUNGSINFORMATIONEN");

        const invoiceData = [
          [
            "Rechnungsnummer",
            project.invoiceNo || project.InvoiceNumber || "N/A",
          ],
          [
            "Rechnungsdatum",
            project.invoiceDate
              ? new Date(project.invoiceDate).toLocaleDateString("de-DE")
              : "N/A",
          ],
          [
            "Rechnungsbetrag",
            `€${parseFloat(project.gesamtCost || 0).toFixed(2)}`,
          ],
          ["Status", "Ausgestellt"],
        ];

        doc.autoTable({
          body: invoiceData,
          startY: yPosition,
          theme: "plain",
          styles: {
            fontSize: 10,
            cellPadding: 3,
          },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 60, textColor: [60, 60, 60] },
            1: { cellWidth: 100 },
          },
          margin: { left: leftMargin, right: 20 },
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // === PROJECT NOTES ===
      const projectNotes =
        projectDetails.Erläuterung ||
        project.Erläuterung ||
        project.description;
      if (projectNotes) {
        checkNewPage(40);
        addSectionHeader("PROJEKTNOTIZEN");

        doc.setFillColor(255, 255, 240);
        doc.roundedRect(leftMargin, yPosition, contentWidth, 50, 3, 3, "F");

        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        const notes = doc.splitTextToSize(projectNotes, contentWidth - 10);
        let noteY = yPosition + 8;
        notes.forEach((line) => {
          if (noteY + 6 > yPosition + 50) return; // Don't overflow the box
          doc.text(line, leftMargin + 5, noteY);
          noteY += 6;
        });

        yPosition += 55;
      }

      // === STATUS CHECKS ===
      checkNewPage(50);
      addSectionHeader("PROJEKTSTATUS-PRÜFUNGEN");

      const statusChecks = [
        {
          label: "Materialeingang",
          status: projectDetails.MaterialIncomingStatus,
          icon: "📦",
        },
        {
          label: "Musterlieferung",
          status: projectDetails.MusterlieferungStatus,
          icon: "📋",
        },
        {
          label: "Werkszeugnisse",
          status: projectDetails.WerkszeugnisseStatus,
          icon: "🔧",
        },
        {
          label: "Transport",
          status: projectDetails.TransportStatus,
          icon: "🚚",
        },
        {
          label: "Materialstatus",
          status: projectDetails.MaterialStatus,
          icon: "📊",
        },
        {
          label: "Meßwerkzeuge",
          status: projectDetails.MeßwerkzeugeStatus,
          icon: "📏",
        },
      ];

      let checkY = yPosition;

      statusChecks.forEach((check, index) => {
        if (index % 2 === 0) {
          checkY = yPosition;
          if (index > 0) yPosition += 12; // move down after every row
        }

        const checkX = index % 2 === 0 ? leftMargin : 105;
        const checkWidth = 80;

        // Checkbox background
        if (check.status) {
          doc.setFillColor(220, 252, 226); // light green
        } else {
          doc.setFillColor(254, 226, 226); // light red
        }
        doc.roundedRect(checkX, checkY, checkWidth, 10, 2, 2, "F");

        // Check mark or X
        doc.setFontSize(10);
        const color = check.status ? successColor : accentColor;
        doc.setTextColor(color[0], color[1], color[2]);
        doc.text(check.status ? "✓" : "✗", checkX + 5, checkY + 7);

        // Label
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        doc.text(check.label, checkX + 12, checkY + 7);
      });
      yPosition += 15;

      // === SUMMARY PAGE ===
      doc.addPage();
      yPosition = 30;

      addSectionHeader("PROJEKTZUSAMMENFASSUNG");

      // Project performance metrics
      const metrics = [
        {
          label: "Zeiteffizienz",
          value: `${(
            (parseFloat(project.percentDone) /
              Math.max(1, summaryData.totalHours)) *
            10
          ).toFixed(1)}%`,
          description: "Fortschritt pro Arbeitsstunde",
        },
        {
          label: "Kosteneffizienz",
          value: isProfitable ? "Profitabel" : "Verlustreich",
          description: `${Math.abs(profitLoss).toFixed(2)}€ ${
            isProfitable ? "Gewinn" : "Verlust"
          }`,
        },
        {
          label: "Materialanteil",
          value: `${(
            (summaryData.materialCost / Math.max(1, summaryData.revenue)) *
            100
          ).toFixed(1)}%`,
          description: "Anteil der Materialkosten am Erlös",
        },
        {
          label: "Arbeitsanteil",
          value: `${(
            (summaryData.laborCost / Math.max(1, summaryData.revenue)) *
            100
          ).toFixed(1)}%`,
          description: "Anteil der Arbeitskosten am Erlös",
        },
      ];

      metrics.forEach((metric, index) => {
        const metricY = yPosition + index * 25;

        // Metric card
        doc.setFillColor(248, 248, 248);
        doc.roundedRect(leftMargin, metricY, contentWidth, 20, 3, 3, "F");

        // Metric label
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(metric.label, leftMargin + 5, metricY + 8);

        // Metric value
        doc.setFontSize(12);
        const metricColor =
          isProfitable && metric.label === "Kosteneffizienz"
            ? successColor
            : !isProfitable && metric.label === "Kosteneffizienz"
            ? accentColor
            : [60, 60, 60];
        doc.setTextColor(metricColor[0], metricColor[1], metricColor[2]);
        doc.text(metric.value, rightMargin - 5, metricY + 8, {
          align: "right",
        });

        // Metric description
        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        doc.setTextColor(120, 120, 120);
        doc.text(metric.description, leftMargin + 5, metricY + 15);
      });

      yPosition += 110;

      // Key recommendations
      checkNewPage(50);
      addSectionHeader("EMPFEHLUNGEN");

      const recommendations = [];

      if (summaryData.progress < 50) {
        recommendations.push(
          "• Projektfortschritt beschleunigen - unter 50% abgeschlossen"
        );
      }
      if (!isProfitable) {
        recommendations.push(
          "• Kostenoptimierung prüfen - Projekt ist derzeit verlustreich"
        );
      }
      if (summaryData.materialCost > summaryData.laborCost * 1.5) {
        recommendations.push(
          "• Materialkosten überprüfen - überdurchschnittlich hoch"
        );
      }
      if (!hasInvoice) {
        recommendations.push(
          "• Rechnung ausstellen - keine Rechnung vorhanden"
        );
      }

      if (recommendations.length > 0) {
        doc.setFillColor(255, 250, 240);
        doc.roundedRect(
          leftMargin,
          yPosition,
          contentWidth,
          recommendations.length * 8 + 10,
          3,
          3,
          "F"
        );

        doc.setFontSize(10);
        doc.setTextColor(100, 60, 0);
        let recY = yPosition + 8;
        recommendations.forEach((rec) => {
          doc.text(rec, leftMargin + 5, recY);
          recY += 8;
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(successColor[0], successColor[1], successColor[2]);
        doc.text(
          "✓ Projekt läuft optimal - keine Handlungsempfehlungen",
          leftMargin + 5,
          yPosition + 8
        );
      }

      // Add final page footer
      addPageFooter();

      // Add page numbers to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Seite ${i} von ${pageCount}`, 105, 290, { align: "center" });
      }

      // Save the PDF
      try {
        doc.save(
          `Projektbericht_${project.ANR}_${
            new Date().toISOString().split("T")[0]
          }.pdf`
        );
        console.log(
          `Successfully generated booklet for project ${project.ANR}`
        );
      } catch (saveError) {
        throw new Error(
          "Fehler beim Speichern der PDF-Datei: " + saveError.message
        );
      }
    } catch (error) {
      console.error("Error generating project booklet:", error);
      let errorMessage = "Fehler beim Erstellen des Projektberichts. ";

      if (error.message) {
        errorMessage += `Details: ${error.message}`;
      } else if (error.response?.data?.message) {
        errorMessage += `API Fehler: ${error.response.data.message}`;
      } else {
        errorMessage += "Bitte versuchen Sie es erneut.";
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Bulk Export Selected Projects Booklets
  const exportSelectedProjectsBooklets = async () => {
    if (selectedProjects.length === 0) {
      alert("Please select projects to export booklets.");
      return;
    }

    try {
      setLoading(true);

      // Find the selected project objects
      const projectsToExport = [];

      filteredMatchedPrefixes?.forEach((prefix) => {
        prefix.matchedProjects.forEach((project) => {
          if (selectedProjects.includes(project._id)) {
            projectsToExport.push(project);
          }
        });
      });

      if (projectsToExport.length === 0) {
        alert("No valid projects found for export.");
        return;
      }

      // Export each project booklet
      for (let i = 0; i < projectsToExport.length; i++) {
        const project = projectsToExport[i];

        // Add a small delay between exports to prevent overwhelming the server
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        await exportProjectBooklet(project);
      }

      alert(
        `Successfully generated ${projectsToExport.length} project booklets!`
      );
    } catch (error) {
      console.error("Error during bulk export:", error);
      alert(
        "Error during bulk export. Some booklets may not have been generated."
      );
    } finally {
      setLoading(false);
    }
  };

  // Modal handling functions
  const openModal = async (anr) => {
    setSelectedANR(anr);
    setModalLoading(true);
    setModalError(null);
    setIsModalOpen(true);

    try {
      // Check if this is a parent project
      const isParent = isParentProject(anr);

      // Get all project data to find subprojects
      const allProjectsResponse = await axios.get(
        `${base_url}/v1/getAllUserProjects`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      const allProjects = allProjectsResponse.data.data || [];
      const allANRs = allProjects.map((p) => p.ANR).filter(Boolean);

      // For parent projects, gather data for all their subprojects as well
      let anrsToFetch = [anr];
      let projectTitle = "";

      if (isParent) {
        // Find all subprojects belonging to this parent
        allProjects.forEach((project) => {
          if (project.ANR === anr && project.Auftragsbezeichnung) {
            projectTitle = project.Auftragsbezeichnung;
          }

          if (project.ANR && isParentOf(anr, project.ANR)) {
            anrsToFetch.push(project.ANR);
          }
        });

        // console.log(
        //   `Found ${anrsToFetch.length - 1} subprojects for parent ${anr}`
        // );
      }

      // Fetch time logs for the main project and all its subprojects
      // Try different endpoints to ensure we get all time logs
      const fetchPromises = anrsToFetch.flatMap((projectANR) => [
        // First try getUserProjectbyANR2
        axios
          .get(`${base_url}/v1/getUserProjectbyANR2/${projectANR}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          })
          .catch((err) => {
            console.log(
              `First endpoint failed for ${projectANR}, trying alternatives`
            );
            return { data: [] };
          }),

        // Also try getUserProjectReport as well
        axios
          .get(`${base_url}/v1/getUserProjectReport/${projectANR}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          })
          .catch((err) => {
            console.log(`Second endpoint failed for ${projectANR}`);
            return { data: [] };
          }),

        // Try projectUsers endpoint directly
        axios
          .get(`${base_url}/v1/getProjectUsers/${projectANR}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          })
          .catch((err) => {
            console.log(`Third endpoint failed for ${projectANR}`);
            return { data: [] };
          }),
      ]);

      // Wait for all requests to complete
      const results = await Promise.all(fetchPromises);
      // console.log("Fetched time logs from multiple endpoints:", results.length);

      // Combine all the time log data
      let allTimeLogs = [];
      let anrIndex = 0;

      for (let i = 0; i < results.length; i += 3) {
        const projectANR = anrsToFetch[anrIndex];
        anrIndex++;

        // Process data from each of the 3 endpoints for this ANR
        for (let j = 0; j < 3; j++) {
          const response = results[i + j];
          if (!response || !response.data) continue;

          const timeLogData = response.data.data || response.data;

          if (Array.isArray(timeLogData) && timeLogData.length > 0) {
            console.log(
              `Found ${
                timeLogData.length
              } time logs for ${projectANR} from endpoint ${j + 1}`
            );

            // Add the ANR info to each time log entry
            const processedLogs = timeLogData.map((log) => ({
              ...log,
              projectANR: projectANR,
              isSubproject: projectANR !== anr,
            }));

            allTimeLogs = [...allTimeLogs, ...processedLogs];
          }
        }
      }

      if (allTimeLogs.length > 0) {
        // Sort by date descending
        allTimeLogs.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || a.DateTime || 0);
          const dateB = new Date(b.date || b.createdAt || b.DateTime || 0);
          return dateB - dateA;
        });

        setModalData(allTimeLogs);

        // Update modal title with project info if available
        if (isParent && projectTitle) {
          setModalTitle(`${anr}: ${projectTitle} (inkl. Unterprojekte)`);
        } else {
          setModalTitle(`Zeiten für ANR: ${anr}`);
        }
      } else {
        setModalData([]);
        setModalError("Keine Zeitbuchungen für dieses Projekt gefunden");
      }
    } catch (err) {
      console.error("Error processing project time logs:", err);
      setModalError("Fehler beim Abrufen der Projektzeiten");
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedANR(null);
    setModalData([]);
    setModalTitle("");
  };

  // Project selection and update handling
  const handleSelectProject = (projectId) => {
    setSelectedProjects((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleInputChange = (projectId, field, value) => {
    setCostEdits((prev) => ({
      ...prev,
      [projectId]: {
        ...(prev[projectId] || {}),
        [field]: value,
      },
    }));
  };

  const handleUpdate = async (projectId) => {
    const edit = costEdits[projectId];
    if (!edit) return;

    try {
      // Find the project in the current data
      let currentProject = null;
      for (const prefix of mergedData.matchedPrefixes) {
        const foundProject = prefix.matchedProjects.find(
          (p) => p._id === projectId
        );
        if (foundProject) {
          currentProject = foundProject;
          break;
        }
      }

      if (!currentProject) {
        throw new Error("Project not found in current data");
      }

      // Prepare data to send to the server
      const updateData = {};

      // Handle gesamtCost (Rechnungserlöß) update
      if (edit.editedGesamtCost !== undefined) {
        // Make sure to convert to number
        const numValue = parseFloat(edit.editedGesamtCost);
        updateData.gesamtCost = numValue;

        // Important: send as both gesamtCost and GessamCost to ensure it sticks
        // Some backend implementations might use different field names
        updateData.GessamCost = numValue;
        updateData.Gesamt_Cost = numValue;

        // This will be saved to editedRechnungCost in the backend
        console.log(
          `Updating Rechnungserlöß (editedRechnungCost) to ${numValue}`
        );
      }

      // Handle MaterialCost update
      if (edit.editedMaterialCost !== undefined) {
        const numValue = parseFloat(edit.editedMaterialCost);
        updateData.MaterialCost = numValue;
        // Also update alternate field names
        updateData.materialCost = numValue;
      }

      // Handle TotalTime update
      if (edit.editedTotalTime !== undefined) {
        const numValue = parseFloat(edit.editedTotalTime);
        updateData.TotalTime = numValue;
        updateData.totalTime = numValue;
      }

      // If the project has an ANR, include it in the update data
      if (currentProject.ANR) {
        updateData.ANR = currentProject.ANR;
        updateData.anr = currentProject.ANR; // Include lowercase version too
      }

      console.log("Updating project with data:", updateData);

      // Send the update to the server
      const response = await axios.put(
        `${base_url}/v1/updateProjectbyID/${projectId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      if (response.data && response.data.status !== 0) {
        console.log("Update successful:", response.data);

        // Update was successful, update the local state
        const updatedMergedData = {
          ...mergedData,
          matchedPrefixes: mergedData.matchedPrefixes.map((prefix) => ({
            ...prefix,
            matchedProjects: prefix.matchedProjects.map((project) => {
              if (project._id === projectId) {
                // Create updated project with all proper fields set
                const updatedProject = {
                  ...project,
                  ...updateData, // Include all fields that were sent to the server

                  // Ensure these fields are explicitly updated for display with formatting
                  gesamtCost:
                    edit.editedGesamtCost !== undefined
                      ? parseFloat(edit.editedGesamtCost).toFixed(2)
                      : project.gesamtCost,
                  MaterialCost:
                    edit.editedMaterialCost !== undefined
                      ? parseFloat(edit.editedMaterialCost).toFixed(2)
                      : project.MaterialCost,
                  TotalTime:
                    edit.editedTotalTime !== undefined
                      ? parseFloat(edit.editedTotalTime).toFixed(2)
                      : project.TotalTime,
                };

                // Recalculate labor cost and profit/loss
                const timeInHours = parseFloat(updatedProject.TotalTime) || 0;
                const hourlyRate = parseFloat(updatedProject.MengeCost || 60);
                const laborCost = timeInHours * hourlyRate;
                const materialCost =
                  parseFloat(updatedProject.MaterialCost) || 0;
                const gesamtCost = parseFloat(updatedProject.gesamtCost) || 0;

                // Add calculated fields
                updatedProject.calculatedLaborCost = laborCost.toFixed(2);
                updatedProject.totalCombinedCost = (
                  laborCost + materialCost
                ).toFixed(2);
                updatedProject.calculatedProfitLoss = (
                  gesamtCost -
                  (laborCost + materialCost)
                ).toFixed(2);

                return updatedProject;
              }
              return project;
            }),
          })),
        };

        // First log the updated data
        console.log("Setting updated merged data:", updatedMergedData);

        // Save to state
        setMergedData(updatedMergedData);

        // Update to localStorage for persistence between refreshes
        try {
          // Save only the essential fields to avoid localStorage size limits
          const storageData = {};

          updatedMergedData.matchedPrefixes.forEach((prefix) => {
            prefix.matchedProjects.forEach((project) => {
              if (project._id) {
                storageData[project._id] = {
                  _id: project._id,
                  ANR: project.ANR,
                  gesamtCost: project.gesamtCost,
                  MaterialCost: project.MaterialCost,
                  TotalTime: project.TotalTime,
                  MengeCost: project.MengeCost,
                  updatedAt: new Date().toISOString(),
                };
              }
            });
          });
        } catch (e) {
          console.warn("Failed to save to localStorage:", e);
        }

        // After update, clear the edits for this project
        setCostEdits((prev) => {
          const { [projectId]: _, ...rest } = prev;
          return rest;
        });

        // Show success message
        alert("Update successful!");
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      alert(`Update failed: ${error.message || "Unknown error"}`);
    }
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // Initialize date range with current month
  useEffect(() => {
    setDateRange(getCurrentMonthRange());
  }, []);

  // Existing API fetch effects remain the same

  // Fetch data from the first API (getAnrMatchesByPrefix)
  const fetchAnrMatches = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getAnrMatchesByPrefix`);
      setData(response.data);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "An error occurred while fetching ANR matches."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data from the second API (getAllUserProjects)
  const fetchAllUserProjects = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getAllUserProjects`);
      const processedData = processProjectData(response.data.data);
      setProjectData(processedData);
    } catch (err) {
      console.error(err);
      setProjectError(
        err.response?.data?.message ||
          "An error occurred while fetching project data."
      );
    } finally {
      setProjectLoading(false);
    }
  };

  // Fetch data from the Rechnungs API (getallRechnungs)
  const fetchAllRechnungs = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getallRechnungs`);
      setRechnungsData(response.data);
    } catch (err) {
      console.error(err);
      setRechnungsError(
        err.response?.data?.message ||
          "An error occurred while fetching Rechnungs data."
      );
    } finally {
      setRechnungsLoading(false);
    }
  };

  const intialFetch = () => {
    // Initiate all fetches
    fetchAnrMatches();
    fetchAllUserProjects();
    fetchAllRechnungs();
    localStorage.clear("rechnungMatcher_projectData");
  };

  useEffect(() => {
    intialFetch();
  }, []);

  const [savedProjectData, setSavedProjectData] = useState({});

  // Once all data is fetched, merge them and calculate totals
  useEffect(() => {
    if (data && projectData && rechnungsData) {
      const materialCostMap = aggregateMaterialCosts(rechnungsData);
      let merged = mergeDataByANR(
        data,
        projectData,
        materialCostMap,
        costEdits
      );

      // Apply saved data from localStorage if available
      if (Object.keys(savedProjectData).length > 0) {
        console.log("Applying saved data to merged data");

        // Create a new merged object with updated values from localStorage
        merged = {
          ...merged,
          matchedPrefixes: merged.matchedPrefixes.map((prefix) => ({
            ...prefix,
            matchedProjects: prefix.matchedProjects.map((project) => {
              // If we have saved data for this project, apply it
              if (project._id && savedProjectData[project._id]) {
                const saved = savedProjectData[project._id];

                // Use saved values or original values
                const updatedProject = {
                  ...project,
                  gesamtCost: saved.gesamtCost || project.gesamtCost,
                  MaterialCost: saved.MaterialCost || project.MaterialCost,
                  TotalTime: saved.TotalTime || project.TotalTime,
                  MengeCost: saved.MengeCost || project.MengeCost,
                };

                // Recalculate derived values
                const timeInHours = parseFloat(updatedProject.TotalTime) || 0;
                const hourlyRate = parseFloat(updatedProject.MengeCost || 60);
                const laborCost = timeInHours * hourlyRate;
                const materialCost =
                  parseFloat(updatedProject.MaterialCost) || 0;
                const gesamtCost = parseFloat(updatedProject.gesamtCost) || 0;

                // Add calculated fields
                updatedProject.calculatedLaborCost = laborCost.toFixed(2);
                updatedProject.totalCombinedCost = (
                  laborCost + materialCost
                ).toFixed(2);
                updatedProject.calculatedProfitLoss = (
                  gesamtCost -
                  (laborCost + materialCost)
                ).toFixed(2);

                return updatedProject;
              }
              return project;
            }),
          })),
        };
      }

      setMergedData(merged);
    }
  }, [data, projectData, rechnungsData, costEdits, savedProjectData]);

  // Modified filteredMatchedPrefixes to include profit/loss filtering and remove duplicates
  const filteredMatchedPrefixes = useMemo(() => {
    if (!mergedData) return [];

    return mergedData.matchedPrefixes
      .map((prefixData) => {
        // Filter projects within each prefix
        let filteredProjects = prefixData.matchedProjects.filter((project) => {
          // Check Year Filter
          const projectYear = new Date(project.createdAt).getFullYear();
          const isYearMatch =
            selectedYear === "all" || projectYear === parseInt(selectedYear);

          // Check Date Range Filter
          let isDateMatch = true;
          if (dateRange.startDate && dateRange.endDate) {
            const projectDate = new Date(project.createdAt);
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);
            isDateMatch = projectDate >= start && projectDate <= end;
          }

          return isYearMatch && isDateMatch;
        });

        // Further filter based on search term and invoice filter
        filteredProjects = filteredProjects.filter((project) => {
          const matchesSearch = project.ANR.toLowerCase().includes(
            searchTerm.toLowerCase()
          );
          let matchesInvoice = true;
          if (invoiceFilter === "withoutInvoice") {
            matchesInvoice = project.invoiceNo === "Keine Rechnung gefunden";
          } else if (invoiceFilter === "withInvoice") {
            matchesInvoice = project.invoiceNo !== "Keine Rechnung gefunden";
          }
          return matchesSearch && matchesInvoice;
        });

        // Update each project's labor cost calculation using MengeCost
        filteredProjects = filteredProjects.map((project) => {
          // Calculate labor cost correctly using MengeCost
          const timeInHours = parseFloat(project.TotalTime) || 0;
          const hourlyRate = parseFloat(project.MengeCost || 60); // Use MengeCost if available, otherwise default to 60€
          const laborCost = timeInHours * hourlyRate;

          // Calculate new profit/loss
          const materialCost = parseFloat(project.MaterialCost) || 0;
          const gesamtCost = parseFloat(project.gesamtCost || 0);
          // Use Auftragserlös if gesamtCost is not available
          const Auftragserlös = parseFloat(project.Auftragserlös || 0);
          const revenue =
            gesamtCost && gesamtCost !== 0 ? gesamtCost : Auftragserlös;
          const profitLoss = revenue - (laborCost + materialCost);

          return {
            ...project,
            calculatedLaborCost: laborCost.toFixed(2),
            calculatedProfitLoss: profitLoss.toFixed(2),
            totalCombinedCost: (laborCost + materialCost).toFixed(2),
          };
        });

        // Apply profit/loss filter
        filteredProjects = filterByProfitLoss(filteredProjects);

        // Remove duplications by keeping only one project per ANR
        // First create a map to store the best project for each ANR
        const anrMap = new Map();

        filteredProjects.forEach((project) => {
          if (!anrMap.has(project.ANR)) {
            anrMap.set(project.ANR, project);
          } else {
            // If this project has an invoice and the existing one doesn't, replace it
            const existingProject = anrMap.get(project.ANR);
            const existingHasInvoice =
              existingProject.invoiceNo !== "Keine Rechnung gefunden";
            const currentHasInvoice =
              project.invoiceNo !== "Keine Rechnung gefunden";

            if (!existingHasInvoice && currentHasInvoice) {
              anrMap.set(project.ANR, project);
            }
            // If both have invoices or both don't, prefer the one with the most recent timestamp
            else if (existingHasInvoice === currentHasInvoice) {
              const existingDate = new Date(existingProject.createdAt || 0);
              const currentDate = new Date(project.createdAt || 0);

              if (currentDate > existingDate) {
                anrMap.set(project.ANR, project);
              }
            }
          }
        });

        // Convert the map back to an array
        const dedupedProjects = Array.from(anrMap.values());

        return { ...prefixData, matchedProjects: dedupedProjects };
      })
      .filter((prefixData) => prefixData.matchedProjects.length > 0);
  }, [
    mergedData,
    selectedYear,
    dateRange,
    searchTerm,
    invoiceFilter,
    profitLossView,
  ]);

  // Calculate grand totals based on filtered data
  useEffect(() => {
    if (filteredMatchedPrefixes && filteredMatchedPrefixes.length > 0) {
      let totalMaterialCost = 0;
      let totalLaborCost = 0;
      let totalGesamtCost = 0;
      let totalSeconds = 0;
      let totalProfitOrLoss = 0;
      let totalCombinedCost = 0; // Total of material + labor costs
      const aggregated = {};

      filteredMatchedPrefixes.forEach((prefix) => {
        let prefixMaterialCost = 0;
        let prefixTotalSeconds = 0;
        let prefixTotalGesamtCost = 0;
        let prefixTotalAuftragserlös = 0;
        let prefixTotalLaborCost = 0;

        prefix.matchedProjects.forEach((project) => {
          // Material cost
          const materialCost = parseFloat(project.MaterialCost) || 0;
          prefixMaterialCost += materialCost;

          // Rechnungserlöß (Revenue)
          const gesamtCost = parseFloat(project.gesamtCost || 0);
          if (gesamtCost === parseFloat(0)) {
            const Auftragserlös = parseFloat(project.Auftragserlös || 0);
            prefixTotalAuftragserlös += Auftragserlös;
          }
          prefixTotalGesamtCost += gesamtCost;

          // Time in seconds
          const timeInHours = parseFloat(project.TotalTime) || 0;
          const seconds = timeInHours * 3600;
          prefixTotalSeconds += seconds;

          // Labor cost - using the calculateLaborCost function with MengeCost
          const laborCost = calculateLaborCost(project);
          prefixTotalLaborCost += laborCost;
        });

        // Combined costs (material + labor)
        const prefixCombinedCost = prefixMaterialCost + prefixTotalLaborCost;

        // Calculate profit/loss for this prefix
        const prefixProfitLoss =
          prefixTotalGesamtCost + prefixTotalAuftragserlös - prefixCombinedCost;

        // Add to grand totals
        totalMaterialCost += prefixMaterialCost;
        totalGesamtCost += prefixTotalGesamtCost;
        totalSeconds += prefixTotalSeconds;
        totalLaborCost += prefixTotalLaborCost;
        totalCombinedCost += prefixCombinedCost;
        totalProfitOrLoss += prefixProfitLoss;

        // Store aggregated data for this prefix
        aggregated[prefix.prefix] = {
          totalMaterialCost: prefixMaterialCost,
          totalSeconds: prefixTotalSeconds,
          TotalAuftragserlös: prefixTotalAuftragserlös,
          totalGesamtCost: prefixTotalGesamtCost,
          totalLaborCost: prefixTotalLaborCost,
          totalCombinedCost: prefixCombinedCost,
          profitLoss: prefixProfitLoss,
        };
      });

      setGrandTotalMaterialCost(totalMaterialCost);
      setGrandTotalErwarteteTotalCost(totalLaborCost); // Using labor cost as expected total cost
      setGrandTotalSeconds(totalSeconds);
      setGrandProfitOrLoss(totalProfitOrLoss);
      setAggregatedProjectData(aggregated);
    } else {
      // Reset totals when no data is available
      setGrandTotalMaterialCost(0);
      setGrandTotalErwarteteTotalCost(0);
      setGrandTotalSeconds(0);
      setGrandProfitOrLoss(0);
      setAggregatedProjectData({});
    }
  }, [filteredMatchedPrefixes]);

  // Effect to select all projects by default once mergedData is set
  useEffect(() => {
    if (mergedData) {
      // Extract all project IDs from mergedData
      const allProjectIds = mergedData.matchedPrefixes.flatMap((prefix) =>
        prefix.matchedProjects.map((p) => p._id)
      );
      // Set selectedProjects to include all project IDs by default
      setSelectedProjects(allProjectIds);
    }
  }, [mergedData]);

  // If loading or error, show appropriate UI
  if (loading || projectLoading || rechnungsLoading)
    return (
      <div className="anr-matcher-container">
        <p>Loading...</p>
      </div>
    );
  if (error)
    return (
      <div className="anr-matcher-container">
        <p className="error">{error}</p>
      </div>
    );
  if (projectError)
    return (
      <div className="anr-matcher-container">
        <p className="error">{projectError}</p>
      </div>
    );
  if (rechnungsError)
    return (
      <div className="anr-matcher-container">
        <p className="error">{rechnungsError}</p>
      </div>
    );
  if (!mergedData)
    return (
      <div className="anr-matcher-container">
        <p>No data available.</p>
      </div>
    );

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">
              Betriebswirtschaftliche Auswertung
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {showAllTime
                ? "Zeigt alle Zeitdaten an"
                : dateRange.startDate && dateRange.endDate
                ? `Zeigt Daten vom ${new Date(
                    dateRange.startDate
                  ).toLocaleDateString("de-DE")} bis ${new Date(
                    dateRange.endDate
                  ).toLocaleDateString("de-DE")} an`
                : "Zeigt aktuelle Monatsdaten an"}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => exportToExcel()}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Export Excel
            </button>
            <button
              onClick={() => exportToPDF()}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Export PDF
            </button>
            <button
              onClick={() => exportSelectedProjectsBooklets()}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center gap-2"
              disabled={selectedProjects.length === 0}
              title="Export booklets for selected projects"
            >
              <FileText size={16} />
              Export Booklets ({selectedProjects.length})
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div
            className={`relative p-6 border-2 border-solid rounded-lg shadow-md ${
              isDark
                ? `bg-gray-800 border-gray-700`
                : `bg-white border-gray-200`
            }`}
          >
            {!showAllTime && (
              <span className="absolute top-2 right-4 text-xs px-2 py-1 mt-2 bg-blue-100 text-blue-800 rounded-full">
                {dateRange.startDate && dateRange.endDate
                  ? "Zeitraum"
                  : "Monat"}
              </span>
            )}
            <div className="flex justify-between items-start mt-5">
              <h3 className="text-lg font-semibold">Gesamtmaterialkosten</h3>
            </div>
            <p className="text-3xl font-bold text-green-600 mt-2">
              €{grandTotalMaterialCost?.toFixed(2) || "0.00"}
            </p>
            <p
              className={`text-xs  ${
                isDark ? `text-gray-200` : `text-gray-600`
              } mt-2`}
            >
              Summe aller Materialkosten
            </p>
          </div>
          <div
            className={`relative p-6 border-2 border-solid rounded-lg shadow-md ${
              isDark
                ? `bg-gray-800 border-gray-700`
                : `bg-white border-gray-200`
            }`}
          >
            {!showAllTime && (
              <span className="absolute top-2 right-4 text-xs px-2 py-1 mt-2 bg-blue-100 text-blue-800 rounded-full">
                {dateRange.startDate && dateRange.endDate
                  ? "Zeitraum"
                  : "Monat"}
              </span>
            )}
            <div className="flex justify-between items-start mt-5">
              <h3 className="text-lg font-semibold">Gesamte Arbeitskosten</h3>
            </div>
            <p className="text-3xl font-bold text-purple-600 mt-2">
              €{grandTotalErwarteteTotalCost?.toFixed(2) || "0.00"}
            </p>
            <p
              className={`text-xs  ${
                isDark ? `text-gray-200` : `text-gray-600`
              } mt-2`}
            >
              Stunden × Stundensatz (MengeCost)
            </p>
          </div>
          <div
            className={`relative p-6 border-2 border-solid rounded-lg shadow-md ${
              isDark
                ? `bg-gray-800 border-gray-700`
                : `bg-white border-gray-200`
            }`}
          >
            {!showAllTime && (
              <span className="absolute top-2 right-4 text-xs px-2 py-1 mt-2 bg-blue-100 text-blue-800 rounded-full">
                {dateRange.startDate && dateRange.endDate
                  ? "Zeitraum"
                  : "Monat"}
              </span>
            )}
            <div className="flex justify-between items-start mt-5">
              <h3 className="text-lg font-semibold">Gesamtkosten</h3>
            </div>
            <p className="text-3xl font-bold text-orange-600 mt-2">
              €
              {(grandTotalMaterialCost + grandTotalErwarteteTotalCost).toFixed(
                2
              ) || "0.00"}
            </p>
            <p
              className={`text-xs  ${
                isDark ? `text-gray-200` : `text-gray-600`
              } mt-2`}
            >
              Material + Arbeit
            </p>
          </div>
          <div
            className={`relative p-6 border-2 border-solid rounded-lg shadow-md ${
              isDark
                ? `bg-gray-800 border-gray-700`
                : `bg-white border-gray-200`
            }`}
          >
            {!showAllTime && (
              <span className="absolute top-2 right-4 text-xs px-2 py-1 mt-2 bg-blue-100 text-blue-800 rounded-full">
                {dateRange.startDate && dateRange.endDate
                  ? "Zeitraum"
                  : "Monat"}
              </span>
            )}
            <div className="flex justify-between items-start mt-5">
              <h3 className="text-lg font-semibold">Gesamtzeit</h3>
            </div>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {(grandTotalSeconds / 3600).toFixed(2)} Std
            </p>
            <p
              className={`text-xs  ${
                isDark ? `text-gray-200` : `text-gray-600`
              } mt-2`}
            >
              Summe aller Arbeitsstunden
            </p>
          </div>
          <div
            className={`relative p-6 border-2 border-solid rounded-lg shadow-md ${
              isDark
                ? `bg-gray-800 border-gray-700`
                : `bg-white border-gray-200`
            }`}
          >
            {!showAllTime && (
              <span className="absolute top-2 right-4 text-xs px-2 py-1 mt-2 bg-blue-100 text-blue-800 rounded-full">
                {dateRange.startDate && dateRange.endDate
                  ? "Zeitraum"
                  : "Monat"}
              </span>
            )}
            <div className="flex justify-between items-start mt-5">
              <h3 className="text-lg font-semibold">Rechnungserlöß/Profit</h3>
            </div>
            <p
              className={`text-3xl font-bold ${
                grandProfitOrLoss >= 0 ? "text-green-600" : "text-red-600"
              } mt-2`}
            >
              €{grandProfitOrLoss?.toFixed(2) || "0.00"}
            </p>
            <p
              className={`text-xs  ${
                isDark ? `text-gray-200` : `text-gray-600`
              } mt-2`}
            >
              Erlös − (Material + Arbeit)
            </p>
          </div>
        </div>
      </div>

      {/* Filters and View Controls Section */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div
          className={`p-6 border-2 border-solid rounded-lg shadow-md ${
            isDark ? `bg-gray-800 border-gray-700` : `bg-white border-gray-200`
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* View Mode Controls */}
            <div className="flex items-center space-x-4">
              <label className="block text-sm font-medium">View Mode:</label>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("table")}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    viewMode === "table"
                      ? "bg-blue-500 text-white"
                      : "text-gray-700"
                  }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode("cards")}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    viewMode === "cards"
                      ? "bg-blue-500 text-white"
                      : "text-gray-700"
                  }`}
                >
                  Cards
                </button>
              </div>
            </div>

            {/* Profit/Loss Filter */}
            <div className="flex items-center space-x-4">
              <label className="block text-sm font-medium">Profit/Loss:</label>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setProfitLossView("both")}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    profitLossView === "both"
                      ? "bg-blue-500 text-white"
                      : "text-gray-700"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setProfitLossView("profit")}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    profitLossView === "profit"
                      ? "bg-green-500 text-white"
                      : "text-gray-700"
                  }`}
                >
                  Profit
                </button>
                <button
                  onClick={() => setProfitLossView("loss")}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    profitLossView === "loss"
                      ? "bg-red-500 text-white"
                      : "text-gray-700"
                  }`}
                >
                  Loss
                </button>
              </div>
            </div>

            {/* Invoice Filter */}
            <div>
              <label className="block text-sm font-medium">
                Rechnungsfilter
              </label>
              <select
                value={invoiceFilter}
                onChange={(e) => setInvoiceFilter(e.target.value)}
                className="mt-1 block w-full rounded border-gray-300 shadow-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="withoutInvoice">Ohne Rechnung</option>
                <option value="withInvoice">Mit Rechnung</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium">Suche</label>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            {/* Time Range Selector */}
            <div>
              <label className="block text-sm font-medium">Datumsbereich</label>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newShowAllTime = !showAllTime;
                    setShowAllTime(newShowAllTime);

                    if (newShowAllTime) {
                      // When switching to "Alle Zeit", clear date range
                      setDateRange({ startDate: "", endDate: "" });
                    } else {
                      // When switching to date range, set to current month
                      setDateRange(getCurrentMonthRange());
                    }
                  }}
                  className={`mt-1 px-3 py-2 rounded text-sm ${
                    showAllTime
                      ? "bg-gray-200 text-gray-700"
                      : "bg-blue-500 text-white"
                  }`}
                >
                  {showAllTime ? "Aktueller Monat" : "Alle Zeit"}
                </button>
              </div>
            </div>

            {/* Date Range */}
            {!showAllTime && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium">
                      Startdatum
                    </label>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded border-gray-300 text-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Enddatum
                    </label>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded border-gray-300 text-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto px-4 pb-6">
        {/* Matched Prefixes Section */}
        <div
          className={`rounded-lg shadow-lg mb-6 ${
            isDark ? `bg-gray-800 border-gray-700` : `bg-white border-gray-200`
          }`}
        >
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Passende Präfixe</h2>
          </div>

          {filteredMatchedPrefixes.length > 0 ? (
            filteredMatchedPrefixes.map((prefixData) => {
              // Reset row counter for each prefix
              resetGlobalRowCounter();

              return (
                <div key={prefixData.prefix} className="mb-8">
                  <div
                    className={`px-6 py-3 border-b ${
                      isDark
                        ? `bg-gray-850 border-gray-700`
                        : `bg-gray-50 border-gray-200`
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium">
                        Prefix: {prefixData.prefix} (
                        {prefixData.matchedProjects.length} Projects)
                      </h3>

                      {aggregatedProjectData[prefixData.prefix] && (
                        <div className="flex gap-4 text-sm">
                          <div className="flex flex-col items-end">
                            <span className="text-gray-500">Gesamtzeit</span>
                            <span className="font-medium">
                              {(
                                (aggregatedProjectData[prefixData.prefix]
                                  .totalSeconds || 0) / 3600
                              ).toFixed(2)}{" "}
                              Std
                            </span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-gray-500">Material</span>
                            <span className="font-medium">
                              €
                              {(
                                aggregatedProjectData[prefixData.prefix]
                                  .totalMaterialCost || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-gray-500">
                              Rechnungserlöß
                            </span>
                            <span className="font-medium">
                              €
                              {(
                                aggregatedProjectData[prefixData.prefix]
                                  .totalGesamtCost || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-gray-500">Profit/Loss</span>
                            <span
                              className={`font-medium ${
                                aggregatedProjectData[prefixData.prefix]
                                  .profitLoss >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              €
                              {(
                                aggregatedProjectData[prefixData.prefix]
                                  .profitLoss || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {viewMode === "cards" ? (
                    // Card View
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                      {prefixData.matchedProjects.map((project) => {
                        const profitLoss = calculateProjectProfitLoss(project);
                        const isExpanded =
                          expandedProjects[project._id] || false;
                        const edit = costEdits[project._id] || {};
                        const hasInvoice =
                          project.invoiceNo !== "Keine Rechnung gefunden";

                        return (
                          <div
                            key={project._id}
                            className={`border rounded-lg shadow-sm overflow-hidden ${
                              profitLoss >= 0
                                ? "border-green-300"
                                : "border-red-300"
                            }`}
                          >
                            {/* Card Header with Profit/Loss Indicator */}
                            <div
                              className={`p-4 ${
                                profitLoss >= 0 ? "bg-green-50" : "bg-red-50"
                              } flex justify-between items-center`}
                            >
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.includes(
                                    project._id
                                  )}
                                  onChange={() =>
                                    handleSelectProject(project._id)
                                  }
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                />
                                <button
                                  onClick={() => openModal(project.ANR)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {project.ANR}
                                </button>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    profitLoss >= 0
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {profitLoss >= 0 ? "Profit" : "Loss"}: €
                                  {Math.abs(profitLoss).toFixed(2)}
                                </div>
                                <button
                                  onClick={() =>
                                    toggleProjectExpansion(project._id)
                                  }
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  {isExpanded ? "↑" : "↓"}
                                </button>
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-4 bg-white">
                              <div className="mb-3">
                                <div className="text-sm text-gray-500">
                                  Auftragsbezeichnung
                                </div>
                                <div className="font-medium">
                                  {project.Auftragsbezeichnung}
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <div className="text-sm text-gray-500">
                                    Status
                                  </div>
                                  <div className="font-medium">
                                    {project.status}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500">
                                    Rechnung
                                  </div>
                                  <div className="font-medium">
                                    {project.invoiceNo}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <div className="text-sm text-gray-500">
                                    Gesamter Zeitaufwand
                                  </div>
                                  <div className="font-medium">
                                    {project.TotalTime} Std
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500">
                                    Rechnungserlöß
                                  </div>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={
                                      edit.editedGesamtCost ||
                                      project.gesamtCost
                                    }
                                    onChange={(e) =>
                                      handleInputChange(
                                        project._id,
                                        "editedGesamtCost",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              </div>

                              {/* Expanded Content */}
                              {isExpanded && (
                                <div className="mt-4 pt-4 border-t">
                                  <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                      <div className="text-sm text-gray-500">
                                        Materialkosten
                                      </div>
                                      {!hasInvoice ? (
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={
                                            edit.editedMaterialCost ||
                                            project.MaterialCost
                                          }
                                          onChange={(e) =>
                                            handleInputChange(
                                              project._id,
                                              "editedMaterialCost",
                                              e.target.value
                                            )
                                          }
                                          className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                                        />
                                      ) : (
                                        <div className="font-medium">
                                          €{project.MaterialCost}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-500">
                                        Profit/Loss
                                      </div>
                                      <div
                                        className={`font-medium ${
                                          profitLoss >= 0
                                            ? "text-green-600"
                                            : "text-red-600"
                                        }`}
                                      >
                                        €{profitLoss.toFixed(2)}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        (R: €
                                        {parseFloat(
                                          project.gesamtCost || 0
                                        ).toFixed(2)}{" "}
                                        - M: €
                                        {parseFloat(
                                          project.MaterialCost || 0
                                        ).toFixed(2)}{" "}
                                        - L: €
                                        {calculateLaborCost(project).toFixed(2)}
                                        )
                                      </div>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <div className="text-sm text-gray-500">
                                        Erlöß aus Angebot
                                      </div>
                                      <div className="font-medium">
                                        €{project.ErlossAusAngebot}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-sm text-gray-500">
                                        % Fortschritt
                                      </div>
                                      <div className="font-medium">
                                        {project.percentDone}%
                                      </div>
                                    </div>
                                  </div>

                                  {!hasInvoice && (
                                    <div className="mt-4">
                                      <button
                                        onClick={() =>
                                          handleUpdate(project._id)
                                        }
                                        className="w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                      >
                                        Update
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Table View (Original Design)
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        {/* Column Letters */}
                        <tr
                          className={`${
                            isDark
                              ? `bg-gray-850 border-gray-700`
                              : `bg-gray-50 border-gray-200`
                          }`}
                        >
                          <th className="px-3 py-2 text-xs font-medium text-gray-500">
                            P/L
                          </th>
                          <th className="px-3 py-2 text-xs font-medium text-gray-500">
                            A
                          </th>
                          <th className="px-3 py-2 text-xs font-medium text-gray-500">
                            B
                          </th>
                          {allColumns.map((_, index) => (
                            <th
                              key={index}
                              className="px-3 py-2 text-xs font-medium text-gray-500"
                            >
                              {String.fromCharCode(67 + index)}
                            </th>
                          ))}
                        </tr>

                        {/* Column Headers */}
                        <tr
                          className={`${
                            isDark
                              ? `bg-gray-850 border-gray-700`
                              : `bg-gray-50 border-gray-200`
                          }`}
                        >
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                            Profit/Loss
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                            Select
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                            #
                          </th>
                          {allColumns.map((col) => (
                            <th
                              key={col.key}
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                            >
                              {col.label}
                            </th>
                          ))}
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200">
                        {prefixData.matchedProjects.map((project) => {
                          const edit = costEdits[project._id] || {};
                          const hasInvoice =
                            project.invoiceNo !== "Keine Rechnung gefunden";
                          const profitLoss =
                            calculateProjectProfitLoss(project);

                          return (
                            <tr
                              key={project._id}
                              className={`${
                                isDark
                                  ? "hover:bg-gray-800 hover:text-gray-50"
                                  : "hover:bg-gray-50"
                              } ${
                                profitLoss >= 0 ? "bg-green-50" : "bg-red-50"
                              }`}
                            >
                              {/* Profit/Loss Indicator */}
                              <td className="px-3 py-2">
                                <div
                                  className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${
                                    profitLoss >= 0
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  €{profitLoss.toFixed(2)}
                                </div>
                              </td>

                              {/* Checkbox */}
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.includes(
                                    project._id
                                  )}
                                  onChange={() =>
                                    handleSelectProject(project._id)
                                  }
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                />
                              </td>

                              {/* Row Number */}
                              <td className="px-3 py-2 text-sm text-gray-500">
                                {getGlobalRowNumber()}
                              </td>

                              {/* ANR */}
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => openModal(project.ANR)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {project.ANR}
                                </button>
                              </td>

                              {/* Other Columns */}
                              {allColumns.slice(1).map((col) => (
                                <td key={col.key} className="px-3 py-2">
                                  {col.key === "TotalTime" ? (
                                    !hasInvoice ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={
                                          edit.editedTotalTime ||
                                          project.TotalTime
                                        }
                                        onChange={(e) =>
                                          handleInputChange(
                                            project._id,
                                            "editedTotalTime",
                                            e.target.value
                                          )
                                        }
                                        className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                                      />
                                    ) : (
                                      `${project.TotalTime} Std`
                                    )
                                  ) : col.key === "calculatedLaborCost" ? (
                                    <span
                                      className={`text-sm font-medium ${
                                        parseFloat(
                                          project.calculatedLaborCost || 0
                                        ) > 0
                                          ? "text-purple-700"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      €
                                      {project.calculatedLaborCost ||
                                        (
                                          parseFloat(project.TotalTime || 0) *
                                          parseFloat(project.MengeCost || 60)
                                        ).toFixed(2)}
                                      <span className="text-xs text-gray-500 ml-1">
                                        ({project.TotalTime} ×{" "}
                                        {project.MengeCost || 60}€)
                                      </span>
                                    </span>
                                  ) : col.key === "MaterialCost" ? (
                                    !hasInvoice ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={
                                          edit.editedMaterialCost ||
                                          project.MaterialCost
                                        }
                                        onChange={(e) =>
                                          handleInputChange(
                                            project._id,
                                            "editedMaterialCost",
                                            e.target.value
                                          )
                                        }
                                        className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                                      />
                                    ) : (
                                      `€${project.MaterialCost || 0}`
                                    )
                                  ) : col.key === "totalCombinedCost" ? (
                                    <span className="text-sm font-medium text-orange-600">
                                      €
                                      {project.totalCombinedCost ||
                                        (
                                          parseFloat(
                                            project.MaterialCost || 0
                                          ) +
                                          parseFloat(project.TotalTime || 0) *
                                            parseFloat(project.MengeCost || 60)
                                        ).toFixed(2)}
                                    </span>
                                  ) : col.key === "calculatedProfitLoss" ? (
                                    <span
                                      className={`text-sm font-medium ${
                                        parseFloat(
                                          project.calculatedProfitLoss || 0
                                        ) >= 0
                                          ? "text-green-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      €
                                      {project.calculatedProfitLoss ||
                                        (
                                          parseFloat(project.gesamtCost || 0) -
                                          (parseFloat(
                                            project.MaterialCost || 0
                                          ) +
                                            parseFloat(project.TotalTime || 0) *
                                              parseFloat(
                                                project.MengeCost || 60
                                              ))
                                        ).toFixed(2)}
                                    </span>
                                  ) : col.key === "gesamtCost" ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={
                                        edit.editedGesamtCost ||
                                        project.gesamtCost
                                      }
                                      onChange={(e) =>
                                        handleInputChange(
                                          project._id,
                                          "editedGesamtCost",
                                          e.target.value
                                        )
                                      }
                                      className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                                    />
                                  ) : col.key === "Auftragserlös" ? (
                                    <span
                                      type="number"
                                      step="0.01"
                                      className="inline-flex w-full justify-center items-center text-sm font-medium text-gray-900"
                                    >
                                      {edit.editedGesamtCost ||
                                      (project.gesamtCost &&
                                        project.gesamtCost != parseFloat(0))
                                        ? "‒"
                                        : project.Auftragserlös}
                                    </span>
                                  ) : col.key === "invoiceNo" ? (
                                    <div className="flex items-center w-full">
                                      <span className="w-4/5 text-sm text-gray-900">
                                        {project[col.key]}
                                      </span>
                                      <div className="w-1/5 flex justify-end">
                                        <Pencil
                                          size={14}
                                          className="cursor-pointer text-gray-400 hover:text-gray-600"
                                          onClick={() =>
                                            handleUpdateInvoiceClick(
                                              project._id,
                                              project[col.key]
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    // Display other columns
                                    <span className="text-sm text-gray-900">
                                      {typeof project[col.key] === "number"
                                        ? project[col.key].toFixed(2)
                                        : project[col.key]}
                                    </span>
                                  )}
                                </td>
                              ))}

                              {/* Actions */}
                              <td className="px-3 py-2">
                                <div className="flex gap-2">
                                  {(
                                    <button
                                      onClick={() => handleUpdate(project._id)}
                                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                    >
                                      Update
                                    </button>
                                  )}
                                  <button
                                    onClick={() =>
                                      exportProjectBooklet(project)
                                    }
                                    className="inline-flex items-center gap-1 px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                    title="Export Comprehensive Project Booklet"
                                  >
                                    <FileText size={16} />
                                    Booklet
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}

                        {/* Totals Row */}
                        <tr
                          className={`${
                            isDark ? `bg-gray-800` : `bg-gray-50`
                          } font-medium`}
                        >
                          <td colSpan={3} className="px-3 py-2 text-sm">
                            Prefix Total
                          </td>
                          {allColumns.map((col) => {
                            const prefixAggData =
                              aggregatedProjectData[prefixData.prefix] || {};
                            const totalHours =
                              (prefixAggData.totalSeconds || 0) / 3600;
                            const prefixProfitLoss =
                              prefixAggData.profitLoss || 0;
                            const totalCombined =
                              (prefixAggData.totalMaterialCost || 0) +
                              (prefixAggData.totalLaborCost || 0);

                            // Render different data based on column key
                            if (col.key === "TotalTime") {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm font-medium"
                                >
                                  {totalHours.toFixed(2)} Std
                                </td>
                              );
                            } else if (col.key === "MaterialCost") {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm font-medium"
                                >
                                  €
                                  {(
                                    prefixAggData.totalMaterialCost || 0
                                  ).toFixed(2)}
                                </td>
                              );
                            } else if (col.key === "gesamtCost") {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm font-medium"
                                >
                                  €
                                  {(prefixAggData.totalGesamtCost || 0).toFixed(
                                    2
                                  )}
                                </td>
                              );
                            } else if (col.key === "Auftragserlös") {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm font-medium"
                                >
                                  €
                                  {(
                                    prefixAggData.TotalAuftragserlös || 0
                                  ).toFixed(2)}
                                </td>
                              );
                            } else if (col.key === "calculatedLaborCost") {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm font-medium"
                                >
                                  €
                                  {(prefixAggData.totalLaborCost || 0).toFixed(
                                    2
                                  )}
                                </td>
                              );
                            } else if (col.key === "totalCombinedCost") {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm font-medium"
                                >
                                  €{totalCombined.toFixed(2)}
                                </td>
                              );
                            } else if (col.key === "calculatedProfitLoss") {
                              return (
                                <td
                                  key={col.key}
                                  className={`px-3 py-2 text-sm font-medium ${
                                    prefixProfitLoss >= 0
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  €{prefixProfitLoss.toFixed(2)}
                                </td>
                              );
                            } else {
                              return (
                                <td
                                  key={col.key}
                                  className="px-3 py-2 text-sm"
                                ></td>
                              );
                            }
                          })}
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No matched prefixes found for the current filters.
              </p>
            </div>
          )}
        </div>

        {/* Profit/Loss Summary Section */}
        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Profit/Loss Overview
            </h2>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Profit Projects */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-medium text-green-800 mb-3">
                  Profitable Projects
                </h3>
                <div className="bg-white rounded-lg p-4">
                  {filteredMatchedPrefixes
                    .flatMap((p) => p.matchedProjects)
                    .filter(
                      (project) => calculateProjectProfitLoss(project) >= 0
                    ).length > 0 ? (
                    <div>
                      <div className="mb-2 text-gray-700">
                        <span className="font-medium">Total Projects:</span>{" "}
                        {
                          filteredMatchedPrefixes
                            .flatMap((p) => p.matchedProjects)
                            .filter(
                              (project) =>
                                calculateProjectProfitLoss(project) >= 0
                            ).length
                        }
                      </div>
                      <div className="mb-2 text-gray-700">
                        <span className="font-medium">Total Profit:</span> €
                        {filteredMatchedPrefixes
                          .flatMap((p) => p.matchedProjects)
                          .filter(
                            (project) =>
                              calculateProjectProfitLoss(project) >= 0
                          )
                          .reduce(
                            (sum, project) =>
                              sum + calculateProjectProfitLoss(project),
                            0
                          )
                          .toFixed(2)}
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>Highest Profit:</div>
                        <div className="text-right text-green-600 font-medium">
                          €
                          {Math.max(
                            ...filteredMatchedPrefixes
                              .flatMap((p) => p.matchedProjects)
                              .filter(
                                (project) =>
                                  calculateProjectProfitLoss(project) >= 0
                              )
                              .map((project) =>
                                calculateProjectProfitLoss(project)
                              )
                          ).toFixed(2)}
                        </div>
                        <div>Average Profit:</div>
                        <div className="text-right text-green-600 font-medium">
                          €
                          {(
                            filteredMatchedPrefixes
                              .flatMap((p) => p.matchedProjects)
                              .filter(
                                (project) =>
                                  calculateProjectProfitLoss(project) >= 0
                              )
                              .reduce(
                                (sum, project) =>
                                  sum + calculateProjectProfitLoss(project),
                                0
                              ) /
                              filteredMatchedPrefixes
                                .flatMap((p) => p.matchedProjects)
                                .filter(
                                  (project) =>
                                    calculateProjectProfitLoss(project) >= 0
                                ).length || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center">
                      No profitable projects found.
                    </p>
                  )}
                </div>
              </div>

              {/* Loss Projects */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="text-lg font-medium text-red-800 mb-3">
                  Loss-Making Projects
                </h3>
                <div className="bg-white rounded-lg p-4">
                  {filteredMatchedPrefixes
                    .flatMap((p) => p.matchedProjects)
                    .filter(
                      (project) => calculateProjectProfitLoss(project) < 0
                    ).length > 0 ? (
                    <div>
                      <div className="mb-2 text-gray-700">
                        <span className="font-medium">Total Projects:</span>{" "}
                        {
                          filteredMatchedPrefixes
                            .flatMap((p) => p.matchedProjects)
                            .filter(
                              (project) =>
                                calculateProjectProfitLoss(project) < 0
                            ).length
                        }
                      </div>
                      <div className="mb-2 text-gray-700">
                        <span className="font-medium">Total Loss:</span> €
                        {Math.abs(
                          filteredMatchedPrefixes
                            .flatMap((p) => p.matchedProjects)
                            .filter(
                              (project) =>
                                calculateProjectProfitLoss(project) < 0
                            )
                            .reduce(
                              (sum, project) =>
                                sum + calculateProjectProfitLoss(project),
                              0
                            )
                        ).toFixed(2)}
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div>Highest Loss:</div>
                        <div className="text-right text-red-600 font-medium">
                          €
                          {Math.abs(
                            Math.min(
                              ...filteredMatchedPrefixes
                                .flatMap((p) => p.matchedProjects)
                                .filter(
                                  (project) =>
                                    calculateProjectProfitLoss(project) < 0
                                )
                                .map((project) =>
                                  calculateProjectProfitLoss(project)
                                )
                            )
                          ).toFixed(2)}
                        </div>
                        <div>Average Loss:</div>
                        <div className="text-right text-red-600 font-medium">
                          €
                          {Math.abs(
                            filteredMatchedPrefixes
                              .flatMap((p) => p.matchedProjects)
                              .filter(
                                (project) =>
                                  calculateProjectProfitLoss(project) < 0
                              )
                              .reduce(
                                (sum, project) =>
                                  sum + calculateProjectProfitLoss(project),
                                0
                              ) /
                              filteredMatchedPrefixes
                                .flatMap((p) => p.matchedProjects)
                                .filter(
                                  (project) =>
                                    calculateProjectProfitLoss(project) < 0
                                ).length || 0
                          ).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center">
                      No loss-making projects found.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            {/* Modal Content */}
            <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-medium">
                  {modalTitle || `Projekte für ANR: ${selectedANR}`}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {modalLoading ? (
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : modalError ? (
                  <p className="text-red-600">{modalError}</p>
                ) : modalData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          {[
                            "Datum",
                            "Mitarbeiter",
                            "ANR",
                            "Beschreibung",
                            "Startzeit",
                            "Endzeit",
                            "Gesamtzeit",
                            "Menge",
                            "Arbeiten",
                          ].map((header, index) => (
                            <th
                              key={index}
                              className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {modalData.map((project, index) => {
                          // Format date if available
                          const formattedDate = project.date
                            ? new Date(project.date).toLocaleDateString("de-DE")
                            : project.createdAt
                            ? new Date(project.createdAt).toLocaleDateString(
                                "de-DE"
                              )
                            : project.DateTime // Check for DateTime field (common in time logs)
                            ? new Date(project.DateTime).toLocaleDateString(
                                "de-DE"
                              )
                            : "N/A";

                          // Get user name - might be in different locations depending on API response
                          const userName = project.user
                            ? project.user
                            : project.user_id && project.user_id.name
                            ? project.user_id.name
                            : project.name // Check for name field directly
                            ? project.name
                            : project.userName // Check for userName field
                            ? project.userName
                            : "N/A";

                          // Calculate working hours if not already provided
                          let workingTime = project.TotalTime;
                          if (
                            !workingTime &&
                            project.StartTime &&
                            project.EndTime
                          ) {
                            const startParts =
                              project.StartTime.split(":").map(Number);
                            const endParts =
                              project.EndTime.split(":").map(Number);

                            if (
                              startParts.length === 3 &&
                              endParts.length === 3
                            ) {
                              const startSeconds =
                                startParts[0] * 3600 +
                                startParts[1] * 60 +
                                startParts[2];
                              const endSeconds =
                                endParts[0] * 3600 +
                                endParts[1] * 60 +
                                endParts[2];

                              let diffSeconds = endSeconds - startSeconds;
                              if (diffSeconds < 0) diffSeconds += 24 * 3600;

                              const hours = Math.floor(diffSeconds / 3600);
                              const minutes = Math.floor(
                                (diffSeconds % 3600) / 60
                              );
                              const seconds = diffSeconds % 60;

                              workingTime = `${String(hours).padStart(
                                2,
                                "0"
                              )}:${String(minutes).padStart(2, "0")}:${String(
                                seconds
                              ).padStart(2, "0")}`;
                            }
                          } else if (!workingTime && project.totalTime) {
                            // Check for alternative time fields
                            workingTime = project.totalTime;
                          } else if (!workingTime && project.hours) {
                            // If hours field exists
                            const hours = Math.floor(project.hours);
                            const minutesFraction =
                              (project.hours - hours) * 60;
                            const minutes = Math.floor(minutesFraction);
                            const seconds = Math.floor(
                              (minutesFraction - minutes) * 60
                            );

                            workingTime = `${String(hours).padStart(
                              2,
                              "0"
                            )}:${String(minutes).padStart(2, "0")}:${String(
                              seconds
                            ).padStart(2, "0")}`;
                          }

                          // Determine if this is a subproject entry
                          const isSubproject = project.isSubproject;
                          const projectANR =
                            project.projectANR || project.ANR || "";

                          return (
                            <tr
                              key={index}
                              className={`hover:bg-gray-50 ${
                                isSubproject ? "bg-blue-50" : ""
                              }`}
                            >
                              <td className="px-3 py-2 text-sm">
                                {formattedDate}
                              </td>
                              <td className="px-3 py-2 text-sm font-medium">
                                {userName}
                              </td>
                              <td
                                className={`px-3 py-2 text-sm ${
                                  isSubproject
                                    ? "font-medium text-blue-600"
                                    : ""
                                }`}
                              >
                                {projectANR}
                              </td>
                              <td className="px-3 py-2 text-sm">
                                {project.description ||
                                  project.Arbeiten ||
                                  project.work ||
                                  project.task ||
                                  "N/A"}
                              </td>
                              <td className="px-3 py-2 text-sm font-mono">
                                {project.StartTime ||
                                  project.startTime ||
                                  "N/A"}
                              </td>
                              <td className="px-3 py-2 text-sm font-mono">
                                {project.EndTime || project.endTime || "N/A"}
                              </td>
                              <td className="px-3 py-2 text-sm font-medium text-blue-600">
                                {workingTime || "N/A"}
                              </td>
                              <td className="px-3 py-2 text-sm">
                                {project.Menge ||
                                  project.menge ||
                                  project.quantity ||
                                  "N/A"}
                              </td>
                              <td className="px-3 py-2 text-sm">
                                {project.Arbeiten ||
                                  project.arbeit ||
                                  project.work ||
                                  project.notes ||
                                  "N/A"}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Add a total row */}
                        {modalData.length > 0 && (
                          <tr className="bg-gray-50 font-medium">
                            <td
                              colSpan={6}
                              className="px-3 py-3 text-sm text-right border-t-2 border-gray-300"
                            >
                              Gesamtarbeitszeit:
                            </td>
                            <td className="px-3 py-3 text-sm font-medium text-blue-600 border-t-2 border-gray-300">
                              {(() => {
                                // Calculate total time from all entries
                                let totalSeconds = 0;

                                modalData.forEach((project) => {
                                  if (project.TotalTime) {
                                    // If TotalTime is already in HH:MM:SS format
                                    const timeParts =
                                      project.TotalTime.split(":").map(Number);
                                    if (timeParts.length === 3) {
                                      totalSeconds +=
                                        timeParts[0] * 3600 +
                                        timeParts[1] * 60 +
                                        timeParts[2];
                                    }
                                  } else if (
                                    project.StartTime &&
                                    project.EndTime
                                  ) {
                                    // Calculate from start and end times
                                    const startParts =
                                      project.StartTime.split(":").map(Number);
                                    const endParts =
                                      project.EndTime.split(":").map(Number);

                                    if (
                                      startParts.length === 3 &&
                                      endParts.length === 3
                                    ) {
                                      const startSecs =
                                        startParts[0] * 3600 +
                                        startParts[1] * 60 +
                                        startParts[2];
                                      const endSecs =
                                        endParts[0] * 3600 +
                                        endParts[1] * 60 +
                                        endParts[2];

                                      let diffSecs = endSecs - startSecs;
                                      if (diffSecs < 0) diffSecs += 24 * 3600;

                                      totalSeconds += diffSecs;
                                    }
                                  }
                                });

                                // Convert total seconds to hours for cost calculation
                                const totalHours = totalSeconds / 3600;
                                const estimatedCost = (totalHours * 60).toFixed(
                                  2
                                ); // 60€ per hour

                                // Convert total seconds back to HH:MM:SS
                                const hours = Math.floor(totalSeconds / 3600);
                                const minutes = Math.floor(
                                  (totalSeconds % 3600) / 60
                                );
                                const seconds = totalSeconds % 60;

                                // Format with leading zeros
                                return `${String(hours).padStart(
                                  2,
                                  "0"
                                )}:${String(minutes).padStart(2, "0")}:${String(
                                  seconds
                                ).padStart(2, "0")} (${totalHours.toFixed(
                                  2
                                )} Std = ${estimatedCost}€)`;
                              })()}
                            </td>
                            <td
                              colSpan={2}
                              className="px-3 py-3 text-sm border-t-2 border-gray-300"
                            ></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center">
                    Für diese ANR wurden keine Projekte gefunden.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {ShowInvoiceModal && renderInvoiceModal()}
    </div>
  );
};

export default RechnungMatcher;
